import {useState, useEffect, useRef, Fragment} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useParams, useNavigate } from 'react-router-dom';
import getAdminTaskInstanceDetails from '../functions/API/getAdminTaskInstanceDetails.js'
import postInitiateStepSigning from '../functions/API/postInitiateStepSigning.js'
import postCheckStepSigning from '../functions/API/postCheckStepSigning.js'
import postSendStepSigning from '../functions/API/postSendStepSigning.js'
import postCancelStepSigning from '../functions/API/postCancelStepSigning.js'
import postUpdateTaskInstanceStatus from '../functions/API/postUpdateTaskInstanceStatus.js'
import postDeleteTaskInstance from '../functions/API/postDeleteTaskInstance.js'
import postUpdateStepStatus from '../functions/API/postUpdateStepStatus.js'
import postUpdateFieldValue from '../functions/API/postUpdateFieldValue.js'
import postCompleteLastStep from '../functions/API/postCompleteLastStep.js'
import getAssignmentHistory from '../functions/API/getAssignmentHistory.js'
import getTaskMessages from '../functions/API/getTaskMessages.js'
import postSendTaskMessage from '../functions/API/postSendTaskMessage.js'
import postDeleteTaskMessage from '../functions/API/postDeleteTaskMessage.js'
import postReviewStep from '../functions/API/postReviewStep.js'
import postReopenStep from '../functions/API/postReopenStep.js'
import postReopenTaskInstance from '../functions/API/postReopenTaskInstance.js'
import postAPI from '../functions/API/postAPI.js'
import postSetPaymentStepAmount from '../functions/API/postSetPaymentStepAmount.js'
import postInitiateUpload from '../functions/API/postInitiateUpload.js'
import postUploadChunk from '../functions/API/postUploadChunk.js'
import postCompleteUpload from '../functions/API/postCompleteUpload.js'
import postAddInstanceStep from '../functions/API/postAddInstanceStep.js'
import postGetLinkableFields from '../functions/API/postGetLinkableFields.js'
import postLinkToAgreement from '../functions/API/postLinkToAgreement.js'
import AdminSidebar from '../components/admin/AdminSidebar.js';
import StepActionActivity from '../components/StepActionActivity.js';
import getActionDeliveries from '../functions/API/getActionDeliveries.js';
import postRetryActionDelivery from '../functions/API/postRetryActionDelivery.js';
import { toast } from '../lib/toast.js';
import { validateFieldRules } from '../functions/validateFieldRules.js';
import AssignTaskInstanceForm from '../forms/assignTaskInstance.js';
import AddInstanceFieldForm from '../forms/addInstanceField.js';
import AddInstanceStepForm from '../forms/addInstanceStep.js';
import Loader from '../components/loader.js'
import DeleteModal from '../modals/delete.js'
import CompletionMessageModal from '../modals/completionMessage.js'
import ReviewStepModal from '../modals/reviewStep.js'
import ReopenStepModal from '../modals/reopenStep.js'
import ReopenTaskInstanceModal from '../modals/reopenTaskInstance.js'
import LinkToAgreementModal from '../modals/linkToAgreement.js'
import ConfirmationModal from '../modals/confirmation.js'
import CountersignerPicker, { countersignerSummary, countersignerFromInstanceStep, resolvedCountersignerLabel } from '../components/CountersignerPicker.js'
import getAPI from '../functions/API/getAPI.js'
import { Button } from '../components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet'
import {
  ArrowLeft, Play, CheckCircle2, XCircle, UserPlus, Plus, Trash2, Clock,
  FileText, CreditCard, Upload, ExternalLink, FolderOpen, MessageSquare, Send, ChevronDown, Star, Zap, Circle, HelpCircle, RotateCcw, Info, Link2, PenTool, Users, ArrowRight, ArrowUp, X, AlertCircle, Lock
} from 'lucide-react'
import { INSTANCE_STATUSES, STEP_STATUSES, SOURCE_TYPES } from '../constants/taskInstanceStatuses.js'

export default function TaskInstanceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [instance, setInstance] = useState(null);
    const [shouldRefresh, setShouldRefresh] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Sheet states
    const [assignSheetOpen, setAssignSheetOpen] = useState(false);
    const [addFieldSheetOpen, setAddFieldSheetOpen] = useState(false);
    const [selectedStepId, setSelectedStepId] = useState(null);
    const [historySheetOpen, setHistorySheetOpen] = useState(false);
    const [assignmentHistory, setAssignmentHistory] = useState([]);

    // Messages
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);

    // Action (webhook) deliveries for this instance, grouped per step below.
    const [actionDeliveries, setActionDeliveries] = useState([]);
    const [newMessageBody, setNewMessageBody] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    // Admin field editing
    const [adminFieldValues, setAdminFieldValues] = useState({});
    const [submittingStep, setSubmittingStep] = useState(null);

    // Payment amount editing
    const [editingPaymentStepId, setEditingPaymentStepId] = useState(null);
    const [paymentAmountInput, setPaymentAmountInput] = useState('');

    // Admin-actor document upload steps. Keyed by step id — a flow can have more
    // than one upload step open at once, so the picked files and per-file
    // progress can't live in a single flat piece of state.
    const [uploadPickedFiles, setUploadPickedFiles] = useState({});   // { [stepId]: [{ file, name, size }] }
    const [uploadStepState, setUploadStepState] = useState({});       // { [stepId]: 'uploading' | 'error' }
    const [uploadFileProgress, setUploadFileProgress] = useState({}); // { [stepId]: { [index]: { status, progress, error } } }
    const [uploadDragOverStepId, setUploadDragOverStepId] = useState(null);
    const uploadInputRefs = useRef({});

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [fieldToDelete, setFieldToDelete] = useState(null);

    // Completion modal
    const [completionModalOpen, setCompletionModalOpen] = useState(false);
    const [completionModalLoading, setCompletionModalLoading] = useState(false);
    const [pendingLastStep, setPendingLastStep] = useState(null);

    // Review step modal
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewStep, setReviewStep] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);

    // Reopen step modal
    const [reopenModalOpen, setReopenModalOpen] = useState(false);
    const [reopenStep, setReopenStep] = useState(null);
    const [reopenReason, setReopenReason] = useState('');
    const [reopenLoading, setReopenLoading] = useState(false);

    // Reopen task instance modal
    const [reopenInstanceModalOpen, setReopenInstanceModalOpen] = useState(false);
    const [reopenInstanceReason, setReopenInstanceReason] = useState('');
    const [reopenInstanceLoading, setReopenInstanceLoading] = useState(false);

    // Add step sheet
    const [addStepSheetOpen, setAddStepSheetOpen] = useState(false);
    const [addStepAfterPosition, setAddStepAfterPosition] = useState(0);

    // Link to agreement modal
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkableFields, setLinkableFields] = useState([]);
    const [linkTenantUser, setLinkTenantUser] = useState(null);
    const [linkFieldsLoading, setLinkFieldsLoading] = useState(false);
    const [linkLoading, setLinkLoading] = useState(false);

    // Status dropdown
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

    // Outcome card state
    const [selectedOutcome, setSelectedOutcome] = useState(null);
    const [outcomeMessage, setOutcomeMessage] = useState('');
    const [outcomeLoading, setOutcomeLoading] = useState(false);

    // Delete instance modal
    const [deleteInstanceModalOpen, setDeleteInstanceModalOpen] = useState(false);
    const [cancellationRejectMode, setCancellationRejectMode] = useState(false);
    const [cancellationRejectReason, setCancellationRejectReason] = useState('');

    // Cancel signing modal
    const [cancelSigningModalOpen, setCancelSigningModalOpen] = useState(false);
    const [cancelSigningStepId, setCancelSigningStepId] = useState(null);

    // Initiate signing confirmation modal
    const [signingConfirmModalOpen, setSigningConfirmModalOpen] = useState(false);
    const [signingConfirmStep, setSigningConfirmStep] = useState(null);
    const [jointCustomerEmail, setJointCustomerEmail] = useState('');
    // Per-document counter-signer override, seeded from the step's own config
    const [countersignerOverride, setCountersignerOverride] = useState(null);
    const [adminUsers, setAdminUsers] = useState([]);

    // Auto-scroll to the first active step on load (task flows can be long).
    const currentStepRef = useRef(null);
    const hasAutoScrolledRef = useRef(false);

    // The message composer grows with its content (up to a cap, after which it
    // scrolls) and shrinks back to one line once the message is sent.
    const messageInputRef = useRef(null);
    useEffect(() => {
        const el = messageInputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [newMessageBody]);

    // Once the page is scrolled down, fade in the compact header pill and the
    // "back to top" button together. Both stay mounted and toggle opacity so the
    // fade animates in and out (a plain mount/unmount can't transition).
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 300);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
    const scrollToActiveStep = () => currentStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // The floating status dropdown gets its own open-state so it doesn't tangle
    // with the header's (which is scrolled out of view when the pill is showing).
    const [floatingStatusOpen, setFloatingStatusOpen] = useState(false);

    function getExistingFieldValue(field) {
        const type = field.field_type_snapshot;
        if (type === 'BOOLEAN') return field.value_boolean;
        if (type === 'DATE') {
            if (!field.value_date) return '';
            return field.value_date.substring(0, 10);
        }
        if (type === 'NUMBER' || type === 'CURRENCY') return field.value_number != null ? field.value_number : '';
        if (type === 'DROPDOWN') return field.value_text || '';
        if (type === 'MULTI_SELECT') {
            if (field.value_json) {
                try {
                    const parsed = typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json;
                    return Array.isArray(parsed) ? parsed : [];
                } catch { return []; }
            }
            return [];
        }
        return field.value_text || '';
    }

    function initAdminFieldValues(taskInstance) {
        const values = {};
        const steps = taskInstance.steps || [];
        for (const step of steps) {
            if (step.actor_type_snapshot !== 'admin') continue;
            if (step.status === 'COMPLETED' || step.status === 'BLOCKED') continue;
            for (const field of (step.fields || [])) {
                const val = getExistingFieldValue(field);
                if (val !== '' && val !== null && val !== undefined) {
                    values[field.id] = val;
                }
            }
        }
        return values;
    }

    async function GetAPIData() {
        const response = await getAdminTaskInstanceDetails(parseInt(id));
        if (response.response.ok && response.data?.task_instance) {
            const taskInstance = response.data.task_instance;
            setInstance(taskInstance);
            setAdminFieldValues(initAdminFieldValues(taskInstance));
        } else {
            toast.error('Failed to load task instance.');
        }
        setIsLoaded(true);
    }

    useEffect(() => {
        GetAPIData();
    }, [shouldRefresh]);

    // Admin list for the counter-signer override on document signing steps
    useEffect(() => {
        getAPI('admin/users').then(res => {
            if (res.response.ok && Array.isArray(res.data)) {
                setAdminUsers(res.data);
            }
        }).catch(() => {});
    }, []);

    // Load messages + action deliveries on mount and poll
    useEffect(() => {
        if (instance) {
            fetchMessages();
            fetchActionDeliveries();
            const interval = setInterval(() => { fetchMessages(); fetchActionDeliveries(); }, 10000);
            return () => clearInterval(interval);
        }
    }, [instance?.id]);

    function refreshData() {
        setShouldRefresh(shouldRefresh + 1);
    }

    async function handleStatusChange(newStatus) {
        if (actionLoading) return;
        setActionLoading(true);
        setStatusDropdownOpen(false);

        const response = await postUpdateTaskInstanceStatus({
            id: instance.id,
            status: newStatus,
        });

        if (response.response.ok) {
            toast.success('Status updated.');
            refreshData();
        } else {
            toast.error('Failed to update status.');
        }
        setActionLoading(false);
    }

    async function handleDeleteInstance() {
        if (actionLoading) return;
        setActionLoading(true);

        const response = await postDeleteTaskInstance({ id: instance.id });

        if (response.response.ok) {
            toast.success('Task instance deleted.');
            navigate('/taskinstances');
        } else {
            toast.error('Failed to delete task instance.');
        }
        setActionLoading(false);
    }

    // The API is the authority on step ordering. On a flow with step order enforced
    // it rejects work on a step that isn't open yet, and this page could be showing
    // stale state (another admin got there first, or a step was reopened elsewhere),
    // so surface the server's reason rather than a generic failure.
    function reportStepError(response, fallback) {
        const blocked = response?.data?.blocked_reason === 'previous_step_incomplete';
        toast.error(response?.data?.message || fallback);
        if (blocked) refreshData();
        return blocked;
    }

    async function handleStepStatusChange(stepId, newStatus) {
        if (actionLoading) return;
        setActionLoading(true);

        const response = await postUpdateStepStatus({
            id: stepId,
            status: newStatus,
        });

        if (response.response.ok) {
            toast.success('Step completed.');
            refreshData();
        } else {
            reportStepError(response, 'Failed to update step.');
        }
        setActionLoading(false);
    }

    async function handleSetPaymentAmount(stepId) {
        const amount = parseFloat(paymentAmountInput);
        if (!amount || amount < 0.01) {
            toast.error('Payment amount must be at least £0.01.');
            return;
        }
        setActionLoading(true);
        const response = await postSetPaymentStepAmount({
            task_instance_step_id: stepId,
            amount: amount,
        });
        if (response.response.ok) {
            toast.success('Payment amount set successfully.');
            setEditingPaymentStepId(null);
            setPaymentAmountInput('');
            refreshData();
        } else {
            toast.error(response.data?.message || 'Failed to set payment amount.');
        }
        setActionLoading(false);
    }

    function resolveAgreementName(stepConfig) {
        const name = stepConfig?.agreement_name || stepConfig?.template_name || 'Document for Signing';
        return name.replace(/\{\{(.+?)\}\}/g, (_, fieldName) => {
            const trimmed = fieldName.trim();
            for (const step of (instance?.steps || [])) {
                const field = (step.fields || []).find(f => f.name === trimmed);
                if (field) {
                    const val = renderFieldValue(field);
                    return (val && val !== '-') ? (typeof val === 'string' ? val : String(val)) : `[${trimmed}]`;
                }
            }
            return `[${trimmed}]`;
        });
    }

    function resolveFieldMappingValue(mapping) {
        if (mapping.source_type === 'static_value') {
            return mapping.static_value ?? '';
        }
        if (mapping.source_type === 'customer_enters') {
            return null; // customer fills in during signing
        }
        if (mapping.source_type === 'task_data' && mapping.source_step_id && mapping.source_field_id) {
            const sourceStep = (instance?.steps || []).find(s => s.task_flow_step_id === mapping.source_step_id);
            if (!sourceStep) return '(step not found)';
            const sourceField = (sourceStep.fields || []).find(f => f.task_flow_step_field_id === mapping.source_field_id);
            if (!sourceField) return '(field not found)';

            // Multi-select checkbox mapping — show whether the specific option is checked
            if (mapping.option_value && sourceField.field_type_snapshot === 'MULTI_SELECT' && sourceField.value_json) {
                try {
                    const parsed = typeof sourceField.value_json === 'string' ? JSON.parse(sourceField.value_json) : sourceField.value_json;
                    const isSelected = Array.isArray(parsed) && parsed.includes(mapping.option_value);
                    return (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2 py-0.5 ${isSelected ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-500/30' : 'bg-gray-50 text-gray-400 ring-1 ring-inset ring-gray-200 dark:bg-zinc-800 dark:text-gray-500 dark:ring-zinc-600'}`}>
                            {isSelected ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                            {mapping.option_value}
                        </span>
                    );
                } catch {
                    return '(error)';
                }
            }

            const val = renderFieldValue(sourceField);
            return typeof val === 'string' ? val : String(val ?? '-');
        }
        return '';
    }

    function handleInitiateDocumentSigning(step) {
        if (actionLoading) return;
        setSigningConfirmStep(step);
        setJointCustomerEmail('');
        setCountersignerOverride(countersignerFromInstanceStep(step));
        setSigningConfirmModalOpen(true);
    }

    async function confirmInitiateDocumentSigning() {
        if (!signingConfirmStep) return;

        // Validate joint customer email if provided
        if (jointCustomerEmail && !jointCustomerEmail.includes('@')) {
            toast.error('Please enter a valid email address for the joint customer.');
            return;
        }

        const requiresCountersign = signingConfirmStep.step_config_snapshot?.require_admin_countersign;
        if (requiresCountersign && countersignerOverride) {
            if (countersignerOverride.type === 'admin' && !countersignerOverride.adminUserId) {
                toast.error('Please choose which admin counter-signs this document.');
                return;
            }
            if (countersignerOverride.type === 'email' && !countersignerOverride.email.includes('@')) {
                toast.error('Please enter a valid email address for the counter-signer.');
                return;
            }
        }

        setActionLoading(true);
        setSigningConfirmModalOpen(false);
        try {
            const payload = { task_instance_step_id: signingConfirmStep.id };
            if (jointCustomerEmail) {
                payload.joint_customer_email = jointCustomerEmail;
            }
            // Only sent when the admin picks someone other than the step's own
            // choice — otherwise the API resolves the configured counter-signer.
            if (requiresCountersign && countersignerOverride) {
                if (countersignerOverride.type === 'admin' && countersignerOverride.adminUserId) {
                    payload.countersigner_admin_user_id = countersignerOverride.adminUserId;
                } else if (countersignerOverride.type === 'email' && countersignerOverride.email) {
                    payload.countersigner_email = countersignerOverride.email;
                    if (countersignerOverride.name) payload.countersigner_name = countersignerOverride.name;
                }
            }
            const response = await postInitiateStepSigning(payload);
            if (response.response.ok) {
                toast.success('Draft agreement created. Review and send when ready.');
                refreshData();
            } else {
                reportStepError(response, 'Failed to create draft.');
            }
        } catch {
            toast.error('Failed to create draft agreement.');
        }
        setActionLoading(false);
        setSigningConfirmStep(null);
        setCountersignerOverride(null);
    }

    async function handleSendForSigning(stepId) {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            const response = await postSendStepSigning({ task_instance_step_id: stepId });
            if (response.response.ok) {
                toast.success('Document sent for signing.');
                refreshData();
            } else {
                toast.error(response.data?.message || 'Failed to send for signing.');
            }
        } catch {
            toast.error('Failed to send document for signing.');
        }
        setActionLoading(false);
    }

    async function handleReviewDraft(stepId) {
        if (actionLoading) return;
        // Open window immediately (before async fetch) to avoid popup blocker
        const previewWindow = window.open('about:blank', '_blank');
        setActionLoading(true);
        try {
            const apiToken = localStorage.getItem('apiToken');
            const url = `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_API_VERSION}/taskinstances/steps/signing/preview`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + apiToken,
                },
                body: JSON.stringify({ task_instance_step_id: stepId }),
            });
            if (response.ok) {
                const blob = await response.blob();
                const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(pdfBlob);
                if (previewWindow) {
                    previewWindow.location.href = blobUrl;
                }
            } else {
                if (previewWindow) previewWindow.close();
                const data = await response.json().catch(() => null);
                toast.error(data?.message || 'Unable to get document preview.');
            }
        } catch {
            if (previewWindow) previewWindow.close();
            toast.error('Failed to get document preview.');
        }
        setActionLoading(false);
    }

    async function handleCheckSigningStatus(stepId) {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            const response = await postCheckStepSigning({ task_instance_step_id: stepId });
            if (response.response.ok) {
                const status = response.data?.status;
                const currentSigner = response.data?.current_signer;
                if (status === 'signed') {
                    toast.success('Document has been signed.');
                } else if (status === 'draft') {
                    toast.info('Agreement is in draft. Review and send when ready.');
                } else if (status === 'sent') {
                    if (currentSigner === 'admin' && response.data?.signing_url) {
                        toast.info('It\'s your turn to sign.');
                        window.open(response.data.signing_url, '_blank');
                    } else if (currentSigner === 'admin' && response.data?.can_countersign === false) {
                        // Someone else is the nominated counter-signer
                        toast.info(response.data?.message || 'Waiting for the counter-signer to sign.');
                    } else if (currentSigner === 'customer') {
                        toast.info('Waiting for customer to sign.');
                    } else {
                        toast.info('Document is still awaiting signature.');
                        if (response.data?.signing_url) {
                            window.open(response.data.signing_url, '_blank');
                        }
                    }
                } else {
                    toast.info(`Signing status: ${status}`);
                }
                refreshData();
            } else {
                toast.error(response.data?.message || 'Failed to check signing status.');
            }
        } catch {
            toast.error('Failed to check signing status.');
        }
        setActionLoading(false);
    }

    async function handleAdminSign(stepId) {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            const response = await postCheckStepSigning({ task_instance_step_id: stepId });
            if (response.response.ok && response.data?.signing_url) {
                window.open(response.data.signing_url, '_blank');
            } else {
                toast.error(response.data?.message || 'Unable to get signing link.');
            }
            refreshData();
        } catch {
            toast.error('Failed to open signing document.');
        }
        setActionLoading(false);
    }

    function handleCancelDocumentSigning(stepId) {
        if (actionLoading) return;
        setCancelSigningStepId(stepId);
        setCancelSigningModalOpen(true);
    }

    async function confirmCancelDocumentSigning() {
        if (!cancelSigningStepId) return;
        setActionLoading(true);
        try {
            const response = await postCancelStepSigning({ task_instance_step_id: cancelSigningStepId });
            if (response.response.ok) {
                toast.success('Document signing has been cancelled.');
                refreshData();
            } else {
                toast.error(response.data?.message || 'Failed to cancel signing.');
            }
        } catch {
            toast.error('Failed to cancel document signing.');
        }
        setActionLoading(false);
        setCancelSigningStepId(null);
    }

    async function handleViewHistory() {
        const response = await getAssignmentHistory(instance.id);
        if (response.response.ok && response.data?.assignment_history) {
            setAssignmentHistory(response.data.assignment_history);
        }
        setHistorySheetOpen(true);
    }

    // --- Messages ---

    async function fetchMessages() {
        const response = await getTaskMessages(instance?.id || parseInt(id));
        if (response.response.ok && response.data?.messages) {
            setMessages(response.data.messages);
        }
        setMessagesLoading(false);
    }

    // --- Action (webhook) deliveries ---

    async function fetchActionDeliveries() {
        try {
            const response = await getActionDeliveries({ task_instance_id: instance?.id || parseInt(id) });
            if (response.response.ok) {
                setActionDeliveries(response.data.deliveries || []);
            }
        } catch (e) {
            // Non-fatal — the per-step activity simply stays empty.
        }
    }

    async function handleRetryDelivery(deliveryId) {
        try {
            const response = await postRetryActionDelivery(deliveryId);
            if (response.response.ok) {
                toast.success('Delivery queued for retry.');
                fetchActionDeliveries();
            } else {
                toast.error('Could not retry the delivery.');
            }
        } catch (e) {
            toast.error('Could not retry the delivery.');
        }
    }


    async function handleSendMessage() {
        if (!newMessageBody.trim() || sendingMessage) return;
        setSendingMessage(true);

        const response = await postSendTaskMessage({
            task_instance_id: instance.id,
            body: newMessageBody.trim(),
        });
        if (response.response.ok) {
            setNewMessageBody('');
            await fetchMessages();
        } else {
            toast.error('Failed to send message.');
        }
        setSendingMessage(false);
    }

    async function handleDeleteMessage(msgId) {
        const response = await postDeleteTaskMessage(msgId);
        if (response.response.ok) {
            setMessages(messages.filter(m => m.id !== msgId));
        } else {
            toast.error('Failed to delete message.');
        }
    }

    // --- Admin field editing ---

    function updateAdminFieldValue(fieldId, value) {
        setAdminFieldValues(prev => ({ ...prev, [fieldId]: value }));
    }

    function getFieldOptions(field) {
        const rules = field.validation_rules_snapshot;
        if (!rules) return [];
        try {
            const parsed = typeof rules === 'string' ? JSON.parse(rules) : rules;
            return parsed?.options || [];
        } catch { return []; }
    }

    async function handleAdminSubmitStep(step) {
        const fields = step.fields || [];
        const requiredFields = fields.filter(f => f.is_required_snapshot);
        const missing = requiredFields.filter(f => {
            const val = adminFieldValues[f.id];
            if (val === undefined || val === null || val === '') return true;
            if (Array.isArray(val) && val.length === 0) return true;
            return false;
        });

        if (missing.length > 0) {
            toast.error(`Please complete: ${missing.map(f => f.name_snapshot).join(', ')}`);
            return;
        }

        // Enforce field validation rules (regex/pattern, length, ranges, dates)
        // just like the customer view does. Without this admins could submit any
        // value regardless of the field's snapshot rules.
        for (const field of fields) {
            const errors = validateFieldRules(field, adminFieldValues[field.id]);
            if (errors.length > 0) {
                toast.error(`${field.name_snapshot}: ${errors[0]}`);
                return;
            }
        }

        setSubmittingStep(step.id);

        // Save all field values
        for (const field of fields) {
            const val = adminFieldValues[field.id];
            if (val !== undefined && val !== null) {
                await postUpdateFieldValue({ id: field.id, value: val });
            }
        }

        // Mark step complete (outcome card handles completion of the instance)
        await handleStepStatusChange(step.id, 'COMPLETED');
        setSubmittingStep(null);
    }

    // --- Admin document upload steps ---
    //
    // Mirrors the customer flow in OnboardingStepDetail (initiate → chunk → complete).
    // The API accepts these endpoints from an admin against any step of the
    // instance, so nothing server-side changes; the admin just never had a UI.

    function handleUploadFileSelect(step, files) {
        if (!files?.length) return;
        const config = step.step_config_snapshot || {};
        const allowedTypes = config.allowed_types || [];
        const maxSizeMb = config.max_size_mb || 20;
        const maxFiles = config.max_files || 5;
        const alreadyUploaded = Object.values(step.step_result?.files || {}).length;

        const accepted = [];
        for (const file of files) {
            if (alreadyUploaded + (uploadPickedFiles[step.id]?.length || 0) + accepted.length >= maxFiles) {
                toast.error(`You can upload up to ${maxFiles} files on this step.`);
                break;
            }

            if (allowedTypes.length > 0) {
                const ext = file.name.split('.').pop().toLowerCase();
                if (!allowedTypes.some(t => t.toLowerCase() === ext)) {
                    toast.error(`File type .${ext} is not allowed. Allowed: ${allowedTypes.map(t => '.' + t).join(', ')}`);
                    continue;
                }
            }

            if (file.size > maxSizeMb * 1024 * 1024) {
                toast.error(`${file.name} exceeds the ${maxSizeMb}MB limit.`);
                continue;
            }

            accepted.push({ file, name: file.name, size: file.size });
        }

        if (accepted.length === 0) return;

        setUploadPickedFiles(prev => ({ ...prev, [step.id]: [...(prev[step.id] || []), ...accepted] }));
        // Selecting new files after a failed attempt clears the previous error state.
        setUploadStepState(prev => ({ ...prev, [step.id]: undefined }));
    }

    function removeUploadPickedFile(stepId, index) {
        setUploadPickedFiles(prev => ({
            ...prev,
            [stepId]: (prev[stepId] || []).filter((_, i) => i !== index),
        }));
        setUploadFileProgress(prev => {
            const forStep = { ...(prev[stepId] || {}) };
            delete forStep[index];
            return { ...prev, [stepId]: forStep };
        });
    }

    function setUploadProgress(stepId, index, value) {
        setUploadFileProgress(prev => ({
            ...prev,
            [stepId]: { ...(prev[stepId] || {}), [index]: value },
        }));
    }

    function readFileAsBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async function handleAdminUploadAndComplete(step) {
        const picked = uploadPickedFiles[step.id] || [];
        if (picked.length === 0 || uploadStepState[step.id] === 'uploading') return;

        setUploadStepState(prev => ({ ...prev, [step.id]: 'uploading' }));

        try {
            for (let i = 0; i < picked.length; i++) {
                const { file, name, size } = picked[i];

                // A file already pushed to SharePoint by an earlier attempt must not
                // be sent again, or the step ends up with duplicates.
                if (uploadFileProgress[step.id]?.[i]?.status === 'done') continue;

                setUploadProgress(step.id, i, { status: 'uploading', progress: 0 });

                const base64Data = await readFileAsBase64(file);

                const initRes = await postInitiateUpload({
                    task_instance_step_id: step.id,
                    filename: name,
                    file_size: size,
                    file_index: i,
                });

                if (!initRes.response?.ok) {
                    const msg = initRes.data?.message;
                    toast.error(typeof msg === 'string' ? msg : 'Failed to start upload.');
                    setUploadProgress(step.id, i, { status: 'error', progress: 0 });
                    setUploadStepState(prev => ({ ...prev, [step.id]: 'error' }));
                    return;
                }

                const { file_id, chunk_size } = initRes.data;
                const chunkSizeBytes = chunk_size || 3 * 1024 * 1024;
                // Chunk the base64 string on a 3-byte boundary so each slice decodes
                // independently server-side.
                const base64ChunkSize = Math.ceil(chunkSizeBytes / 3) * 4;
                const totalChunks = Math.ceil(base64Data.length / base64ChunkSize);

                for (let c = 0; c < totalChunks; c++) {
                    const chunkStart = c * base64ChunkSize;
                    const chunkData = base64Data.substring(chunkStart, Math.min(chunkStart + base64ChunkSize, base64Data.length));

                    const chunkRes = await postUploadChunk({
                        task_instance_step_id: step.id,
                        file_id,
                        chunk_data: chunkData,
                        chunk_index: c,
                    });

                    if (!chunkRes.response?.ok) {
                        toast.error('Upload failed.');
                        setUploadProgress(step.id, i, { status: 'error', progress: 0 });
                        setUploadStepState(prev => ({ ...prev, [step.id]: 'error' }));
                        return;
                    }

                    setUploadProgress(step.id, i, {
                        status: 'uploading',
                        progress: Math.round(((c + 1) / totalChunks) * 100),
                    });
                }

                // Chunks are staged; the scan and SharePoint push happen on complete.
                setUploadProgress(step.id, i, { status: 'scanning', progress: 100 });
            }

            const completeRes = await postCompleteUpload({ task_instance_step_id: step.id });

            // 422 = one or more files failed the Sophos scan or the SharePoint push.
            // The rest still landed, so mark them done and only flag the rejects.
            if (completeRes.response?.status === 422) {
                const rejectedByName = {};
                for (const rf of (completeRes.data?.rejected_files || [])) {
                    rejectedByName[rf.filename] = rf.reason === 'malicious'
                        ? 'Rejected — flagged as malicious'
                        : (rf.error || 'Upload failed — please retry');
                }
                setUploadFileProgress(prev => {
                    const forStep = { ...(prev[step.id] || {}) };
                    for (let i = 0; i < picked.length; i++) {
                        const reason = rejectedByName[picked[i].name];
                        if (reason) {
                            forStep[i] = { status: 'rejected', progress: 100, error: reason };
                        } else if (forStep[i]?.status === 'scanning') {
                            forStep[i] = { status: 'done', progress: 100 };
                        }
                    }
                    return { ...prev, [step.id]: forStep };
                });
                toast.error('Some files were rejected.');
                setUploadStepState(prev => ({ ...prev, [step.id]: 'error' }));
                return;
            }

            if (!completeRes.response?.ok) {
                const msg = completeRes.data?.message;
                toast.error(typeof msg === 'string' ? msg : 'Failed to complete upload.');
                setUploadFileProgress(prev => {
                    const forStep = { ...(prev[step.id] || {}) };
                    for (const key of Object.keys(forStep)) {
                        if (forStep[key]?.status === 'scanning') forStep[key] = { status: 'error', progress: 100 };
                    }
                    return { ...prev, [step.id]: forStep };
                });
                setUploadStepState(prev => ({ ...prev, [step.id]: 'error' }));
                return;
            }

            // Step is completed server-side — drop the local picked/progress state
            // so the refreshed step renders its uploaded file list instead.
            setUploadPickedFiles(prev => ({ ...prev, [step.id]: [] }));
            setUploadFileProgress(prev => ({ ...prev, [step.id]: {} }));
            setUploadStepState(prev => ({ ...prev, [step.id]: undefined }));
            toast.success('Documents uploaded and step completed.');
            refreshData();
        } catch (e) {
            toast.error('Upload failed.');
            setUploadStepState(prev => ({ ...prev, [step.id]: 'error' }));
        }
    }

    async function handleOutcomeConfirm() {
        if (!selectedOutcome || outcomeLoading) return;
        setOutcomeLoading(true);

        const response = await postUpdateTaskInstanceStatus({
            id: instance.id,
            status: 'COMPLETED',
            completion_outcome: selectedOutcome,
            completion_message: outcomeMessage || null,
        });

        if (response.response.ok) {
            toast.success('Request completed.');
            setSelectedOutcome(null);
            setOutcomeMessage('');
            refreshData();
        } else {
            toast.error('Failed to complete request.');
        }
        setOutcomeLoading(false);
    }

    async function handleCompleteLastStep(outcome, message) {
        if (!pendingLastStep) return;
        setCompletionModalLoading(true);

        const response = await postCompleteLastStep({
            step_id: pendingLastStep.id,
            completion_outcome: outcome,
            completion_message: message || null,
        });

        if (response.response.ok) {
            toast.success('Task instance completed.');
            setCompletionModalOpen(false);
            setPendingLastStep(null);
            refreshData();
        } else {
            reportStepError(response, 'Failed to complete task instance.');
        }
        setCompletionModalLoading(false);
    }

    async function handleStatusCompleteWithOutcome(outcome, message) {
        setCompletionModalLoading(true);

        const response = await postUpdateTaskInstanceStatus({
            id: instance.id,
            status: 'COMPLETED',
            completion_outcome: outcome,
            completion_message: message || null,
        });

        if (response.response.ok) {
            toast.success('Task instance completed.');
            setCompletionModalOpen(false);
            refreshData();
        } else {
            toast.error('Failed to complete task instance.');
        }
        setCompletionModalLoading(false);
    }

    function handleAddField(stepId) {
        setSelectedStepId(stepId);
        setAddFieldSheetOpen(true);
    }

    function handleDeleteField(field) {
        setFieldToDelete(field);
        setDeleteModalOpen(true);
    }

    async function handleApproveStep(step) {
        if (actionLoading) return;
        setActionLoading(true);

        const response = await postReviewStep({
            step_id: step.id,
            action: 'approve',
        });

        if (response.response.ok) {
            toast.success('Step approved.');
            refreshData();
        } else {
            toast.error('Failed to approve step.');
        }
        setActionLoading(false);
    }

    function handleRejectStep(step) {
        setReviewStep(step);
        setReviewModalOpen(true);
    }

    async function handleReviewConfirm(action, message) {
        if (!reviewStep) return;
        setReviewLoading(true);

        const response = await postReviewStep({
            step_id: reviewStep.id,
            action: action,
            message: message,
        });

        if (response.response.ok) {
            toast.success(action === 'reject_resubmit' ? 'Step returned for resubmission.' : 'Request closed.');
            setReviewModalOpen(false);
            setReviewStep(null);
            refreshData();
        } else {
            toast.error('Failed to process review.');
        }
        setReviewLoading(false);
    }

    function handleReopenStep(step) {
        setReopenStep(step);
        setReopenReason('');
        setReopenModalOpen(true);
    }

    async function handleReopenConfirm() {
        if (!reopenStep) return;
        setReopenLoading(true);

        const response = await postReopenStep({
            step_id: reopenStep.id,
            reason: reopenReason || undefined,
        });

        if (response.response.ok) {
            toast.success('Step reopened.');
            setReopenModalOpen(false);
            setReopenStep(null);
            setReopenReason('');
            refreshData();
        } else {
            toast.error('Failed to reopen step.');
        }
        setReopenLoading(false);
    }

    function handleReopenInstance() {
        setReopenInstanceReason('');
        setReopenInstanceModalOpen(true);
    }

    async function handleReopenInstanceConfirm() {
        setReopenInstanceLoading(true);

        const response = await postReopenTaskInstance({
            task_instance_id: instance.id,
            reason: reopenInstanceReason,
        });

        if (response.response.ok) {
            toast.success('Request reopened.');
            setReopenInstanceModalOpen(false);
            setReopenInstanceReason('');
            refreshData();
        } else {
            toast.error('Failed to reopen request.');
        }
        setReopenInstanceLoading(false);
    }

    function handleAddStep(position) {
        setAddStepAfterPosition(position);
        setAddStepSheetOpen(true);
    }

    async function handleOpenLinkModal() {
        setLinkModalOpen(true);
        setLinkFieldsLoading(true);

        const response = await postGetLinkableFields({
            task_instance_id: instance.id,
        });

        if (response.response.ok && response.data) {
            setLinkableFields(response.data.fields || []);
            setLinkTenantUser(response.data.tenant_user || null);
        } else {
            toast.error('Failed to load field values.');
        }
        setLinkFieldsLoading(false);
    }

    async function handleLinkConfirm({ firstname, lastname, dob, agreementreference }) {
        setLinkLoading(true);

        const response = await postLinkToAgreement({
            task_instance_id: instance.id,
            firstname,
            lastname,
            dob,
            agreementreference,
        });

        if (response.response.ok) {
            toast.success('User linked to agreement successfully.');
            setLinkModalOpen(false);
            refreshData();
        } else {
            const errorMsg = response.data?.error || 'Failed to link user to agreement.';
            toast.error(errorMsg);
        }
        setLinkLoading(false);
    }

    function formatDate(dateStr) {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString();
    }

    function formatDateTime(dateStr) {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString();
    }

    // --- Field display ---

    function renderFieldValue(field) {
        const type = field.field_type_snapshot;

        if (type === 'BOOLEAN') {
            return field.value_boolean !== null ? (field.value_boolean ? 'Yes' : 'No') : '-';
        }
        if (type === 'DATE') {
            return field.value_date ? formatDate(field.value_date) : '-';
        }
        if (type === 'NUMBER') {
            return field.value_number != null ? field.value_number : '-';
        }
        if (type === 'CURRENCY') {
            return field.value_number != null ? `£${parseFloat(field.value_number).toFixed(2)}` : '-';
        }
        if (type === 'FILE') {
            return field.value_file_url ? (
                <a href={field.value_file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                    View File <ExternalLink className="h-3 w-3" />
                </a>
            ) : '-';
        }
        if (type === 'DROPDOWN') {
            if (field.value_json) {
                try {
                    const parsed = typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json;
                    return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
                } catch {
                    return field.value_json;
                }
            }
            return '-';
        }
        if (type === 'MULTI_SELECT') {
            if (field.value_json) {
                try {
                    const parsed = typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json;
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return (
                            <div className="flex flex-wrap gap-1">
                                {parsed.map((v, i) => (
                                    <span key={i} className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-600/20 dark:ring-blue-500/30">
                                        {v}
                                    </span>
                                ))}
                            </div>
                        );
                    }
                    return parsed ? String(parsed) : '-';
                } catch {
                    return field.value_json;
                }
            }
            return '-';
        }
        return field.value_text || '-';
    }

    function renderAdminFieldInput(field) {
        const type = field.field_type_snapshot;
        const value = adminFieldValues[field.id] !== undefined ? adminFieldValues[field.id] : '';

        if (type === 'TEXT') {
            return (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                    placeholder={field.placeholder_snapshot || 'Enter text'}
                    className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                />
            );
        }
        if (type === 'TEXTAREA') {
            return (
                <textarea
                    value={value}
                    onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                    placeholder={field.placeholder_snapshot || 'Enter details'}
                    rows={3}
                    className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none dark:bg-zinc-800 dark:text-white"
                />
            );
        }
        if (type === 'NUMBER') {
            return (
                <input
                    type="number"
                    value={value}
                    onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                    placeholder={field.placeholder_snapshot || '0'}
                    className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                />
            );
        }
        if (type === 'CURRENCY') {
            return (
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400">£</span>
                    <input
                        type="number"
                        value={value}
                        onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                        placeholder={field.placeholder_snapshot || '0.00'}
                        step="0.01"
                        className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg pl-7 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                    />
                </div>
            );
        }
        if (type === 'DATE') {
            return (
                <input
                    type="date"
                    value={value}
                    onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                    className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                />
            );
        }
        if (type === 'BOOLEAN') {
            return (
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={() => updateAdminFieldValue(field.id, true)}
                        className={`px-4 py-2 text-sm rounded-lg border transition-colors ${value === true ? 'bg-gray-100 dark:bg-zinc-700 border-gray-400 text-gray-900 dark:text-white font-medium' : 'border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'}`}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        onClick={() => updateAdminFieldValue(field.id, false)}
                        className={`px-4 py-2 text-sm rounded-lg border transition-colors ${value === false ? 'bg-gray-100 dark:bg-zinc-700 border-gray-400 text-gray-900 dark:text-white font-medium' : 'border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'}`}
                    >
                        No
                    </button>
                </div>
            );
        }
        if (type === 'DROPDOWN') {
            const options = getFieldOptions(field);
            return (
                <div className="flex flex-wrap gap-2">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => updateAdminFieldValue(field.id, opt.value)}
                            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${value === opt.value ? 'bg-gray-100 dark:bg-zinc-700 border-gray-400 text-gray-900 dark:text-white font-medium' : 'border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            );
        }
        if (type === 'MULTI_SELECT') {
            const options = getFieldOptions(field);
            const selected = Array.isArray(value) ? value : [];
            return (
                <div className="flex flex-wrap gap-2">
                    {options.map(opt => {
                        const isSelected = selected.includes(opt.value);
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                    const updated = isSelected
                                        ? selected.filter(v => v !== opt.value)
                                        : [...selected, opt.value];
                                    updateAdminFieldValue(field.id, updated);
                                }}
                                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${isSelected ? 'bg-gray-100 dark:bg-zinc-700 border-gray-400 text-gray-900 dark:text-white font-medium' : 'border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'}`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            );
        }
        return (
            <input
                type="text"
                value={value}
                onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
            />
        );
    }

    // Trace a step's visibility conditions back to the fields they read and, with
    // them, the steps whose owner has to answer before the condition can resolve.
    function getConditionSources(step) {
        const conditions = step.conditional_logic_snapshot;
        if (!conditions || !Array.isArray(conditions) || !instance?.steps) return [];
        const owners = {};
        instance.steps.forEach(s => (s.fields || []).forEach(f => { owners[f.id] = { field: f, ownerStep: s }; }));
        return conditions.map(c => owners[c.source_instance_field_id]).filter(Boolean);
    }

    function getConditionFieldNames(step) {
        const names = getConditionSources(step).map(({ field }) => `'${field.name_snapshot}'`);
        return [...new Set(names)];
    }

    // is_conditionally_visible === false carries two very different meanings. Either
    // the condition was evaluated against an answer and didn't match, or nobody has
    // answered the deciding question yet. The API stores both as plain false (a
    // conditional step is created false at taskInstanceController::adminCreateTaskInstance
    // and only flips true once evaluateConditionalVisibility sees a matching value),
    // so tell them apart by whether the step that owns the source field has been
    // submitted. Without this a request the customer hasn't touched shows its whole
    // conditional tail as "Skipped" before a single answer exists.
    function isConditionUndecided(step) {
        const sources = getConditionSources(step);
        if (sources.length === 0) return false;
        return sources.some(({ ownerStep }) => !['COMPLETED', 'PENDING_APPROVAL'].includes(ownerStep.status));
    }

    function getSkipReason(step) {
        const names = getConditionFieldNames(step);
        if (names.length === 0) return null;
        return `Based on ${names.join(' and ')} response`;
    }

    function getPendingConditionReason(step) {
        const names = getConditionFieldNames(step);
        if (names.length === 0) return 'Only shown if its condition is met';
        return `Shown depending on ${names.join(' and ')}`;
    }

    // --- Render ---

    if (!instance && isLoaded) {
        return (
            <AdminSidebar>
                <div className="text-center py-12">
                    <p className="text-sm text-gray-500">Task instance not found.</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/taskinstances')}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Task Instances
                    </Button>
                </div>
            </AdminSidebar>
        );
    }

    const statusInfo = instance ? (INSTANCE_STATUSES[instance.status] || { label: instance.status, color: 'secondary' }) : {};
    const sortedSteps = instance?.steps ? [...instance.steps].sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)) : [];

    // Group webhook deliveries by the step that fired them (newest first, as
    // returned by the endpoint) so each step card shows only its own activity.
    const deliveriesByStep = {};
    actionDeliveries.forEach((d) => {
        if (d.task_instance_step_id) {
            (deliveriesByStep[d.task_instance_step_id] = deliveriesByStep[d.task_instance_step_id] || []).push(d);
        }
    });
    // A step is out of the flow either because its visibility condition never
    // matched, or because an auto-outcome rule routed the flow past it. Neither
    // counts towards progress — the same pair of filters the API applies when it
    // computes progress_percentage for the instance list.
    const isStepOutOfFlow = (s) => !s.is_conditionally_visible || s.is_skipped === true || s.is_skipped === 1;
    const visibleSteps = sortedSteps.filter(s => !isStepOutOfFlow(s));
    const completedCount = visibleSteps.filter(s => s.status === 'COMPLETED').length;
    const totalSteps = visibleSteps.length;

    // Once the request is completed, closed or cancelled nothing on it is
    // outstanding any more: the steps that never got done are history, not work.
    // The API refuses every step action in this state, so rendering them as live
    // only offers the admin buttons that 422.
    const isInstanceDone = ['COMPLETED', 'CANCELLED'].includes(instance?.status);
    const unfinishedCount = isInstanceDone ? totalSteps - completedCount : 0;
    const unfinishedStepReason = instance?.status === 'CANCELLED'
        ? 'Request was cancelled'
        : instance?.completion_outcome === 'negative'
            ? 'Request was closed'
            : 'Request was completed';

    // First step the admin still needs to deal with: the earliest in-flow step
    // that isn't completed. This intentionally includes BLOCKED steps (e.g. a
    // payment step awaiting an amount) and PENDING_APPROVAL steps, since those
    // are exactly what the admin lands here to action — skipping them scrolled
    // the viewport past the step that needs attention. A finished request has no
    // active step at all, so neither the auto-scroll nor the jump button fires.
    const firstActiveStepId = isInstanceDone ? null : sortedSteps.find(s =>
        !isStepOutOfFlow(s) &&
        s.status !== 'COMPLETED'
    )?.id;

    // Auto-scroll to that step once, after the instance has loaded. Guarded so
    // the messages poll / re-renders don't keep yanking the viewport.
    useEffect(() => {
        if (loading || !isLoaded || hasAutoScrolledRef.current) return;
        const el = currentStepRef.current;
        if (!el) return;
        hasAutoScrolledRef.current = true;
        const raf = requestAnimationFrame(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return () => cancelAnimationFrame(raf);
    }, [loading, isLoaded, firstActiveStepId]);

    // Outcome card state
    const allVisibleStepsComplete = visibleSteps.length > 0 && visibleSteps.every(s => s.status === 'COMPLETED');
    // On a flow with step order enforced every admin step can legitimately be locked
    // at once, because the next thing that has to happen is the customer's. Left
    // unexplained, a column of greyed-out cards reads as a fault rather than a wait.
    const orderEnforced = instance?.enforce_admin_step_order === true;
    const liveAdminSteps = orderEnforced && !isInstanceDone
        ? sortedSteps.filter(s => s.actor_type_snapshot === 'admin' && !isStepOutOfFlow(s))
        : [];
    const hasAdminWorkAvailable = liveAdminSteps.some(s => s.is_actionable === true)
        || sortedSteps.some(s => s.status === 'PENDING_APPROVAL');
    const waitingOnStepName = !hasAdminWorkAvailable
        ? (liveAdminSteps.find(s => s.blocked_reason === 'previous_step_incomplete')?.blocked_by_step_name || null)
        : null;

    const isInstanceCompleted = instance?.status === 'COMPLETED';
    const isCancelled = instance?.status === 'CANCELLED';
    const isAutoCompleted = isInstanceCompleted && messages.some(m => m.sender_role === 'system' && (m.subject === 'Request Completed' || m.subject === 'Request Closed'));

    return (
        <>
            {instance && (
                <div className="pointer-events-none fixed inset-x-0 top-0 z-40 lg:pl-64 lg:pr-2">
                  <div className="px-6 lg:px-10">
                    <div className="relative mx-auto max-w-6xl">
                    {/* Soft scrim so scrolling content dissolves into the page
                        behind the pill instead of colliding with it. */}
                    <div
                        aria-hidden="true"
                        className={`pointer-events-none absolute inset-x-0 top-0 h-24 -mx-6 lg:-mx-10 bg-gradient-to-b from-white via-white/85 to-transparent dark:from-zinc-900 dark:via-zinc-900/85 transition-opacity duration-300 ${scrolled ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <div className="relative flex items-start justify-between gap-3 pt-3">
                    <div
                        className={`inline-flex min-w-0 max-w-full items-center gap-2 rounded-full bg-white dark:bg-zinc-800 py-1.5 pl-2 pr-1.5 shadow-xl ring-1 ring-gray-200 dark:ring-zinc-700 transition-opacity duration-300 ${scrolled ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                    >
                        <button
                            onClick={() => navigate('/taskinstances')}
                            aria-label="Back to task instances"
                            title="Back"
                            className="shrink-0 rounded-full p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                        <span className="min-w-0 text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[40vw]">
                            {instance.task_flow_name}
                        </span>
                        {instance.tenant_user_name && (
                            <span className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400 truncate max-w-[22vw]">
                                {instance.tenant_user_name}
                            </span>
                        )}
                        <Badge variant={statusInfo.color} className="hidden sm:inline-flex whitespace-nowrap">{statusInfo.label}</Badge>
                        <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {completedCount}/{totalSteps} steps
                        </span>
                        {firstActiveStepId && (
                            <button
                                onClick={scrollToActiveStep}
                                title="Jump to the active step"
                                className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 dark:bg-white px-3 py-1.5 text-xs font-semibold text-white dark:text-gray-900 hover:opacity-90 transition-opacity whitespace-nowrap"
                            >
                                Go to active step
                                <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                    {/* Reassign + status, kept reachable while scrolled. Hidden on
                        mobile where there's no room beside the pill — the header
                        controls are a scroll away. */}
                    <div className={`hidden shrink-0 items-center gap-2 lg:flex transition-opacity duration-300 ${scrolled ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                        <Button variant="outline" size="sm" onClick={() => setAssignSheetOpen(true)} className="bg-white dark:bg-zinc-800 shadow-xl">
                            <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                            {instance.owner_admin_user_id ? 'Reassign' : 'Assign'}
                        </Button>
                        <div className="relative">
                            <Button variant="outline" size="sm" onClick={() => setFloatingStatusOpen(!floatingStatusOpen)} className="bg-white dark:bg-zinc-800 shadow-xl">
                                <Badge variant={statusInfo.color} className="mr-1.5">{statusInfo.label}</Badge>
                                <ChevronDown className="h-3.5 w-3.5" />
                            </Button>
                            {floatingStatusOpen && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setFloatingStatusOpen(false)} />
                                    <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-gray-200 dark:border-zinc-700 py-1 z-20">
                                        {instance.status === 'NOT_STARTED' && (
                                            <button onClick={() => { setFloatingStatusOpen(false); handleStatusChange('IN_PROGRESS'); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-zinc-700 dark:text-gray-300 flex items-center gap-2">
                                                <Play className="h-3.5 w-3.5" /> Start
                                            </button>
                                        )}
                                        {instance.status !== 'COMPLETED' && instance.status !== 'CANCELLED' && (
                                            <button onClick={() => { setFloatingStatusOpen(false); handleStatusChange('CANCELLED'); }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                                <XCircle className="h-3.5 w-3.5" /> Cancel
                                            </button>
                                        )}
                                        {(instance.status === 'COMPLETED' || instance.status === 'CANCELLED') && (
                                            <button onClick={() => { setFloatingStatusOpen(false); handleReopenInstance(); }} className="w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2">
                                                <RotateCcw className="h-3.5 w-3.5" /> Reopen
                                            </button>
                                        )}
                                        <div className="border-t border-gray-100 dark:border-zinc-700 my-1" />
                                        <button onClick={() => { setFloatingStatusOpen(false); setDeleteInstanceModalOpen(true); }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                            <Trash2 className="h-3.5 w-3.5" /> Delete
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    </div>
                    </div>
                  </div>
                </div>
            )}
            <button
                onClick={scrollToTop}
                aria-label="Back to top"
                title="Back to top"
                className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-white dark:bg-zinc-800 text-gray-600 dark:text-gray-200 shadow-lg ring-1 ring-gray-200 dark:ring-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-opacity duration-300 ${scrolled ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            >
                <ArrowUp className="h-5 w-5" />
            </button>
            <AdminSidebar>
                {loading ? (
                    <Loader style='table' setLoadingState={setLoading} isLoaded={isLoaded}/>
                ) : (
                    <div>
                        {/* Header */}
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex items-start gap-3">
                                <button
                                    onClick={() => navigate('/taskinstances')}
                                    className="mt-1 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                                >
                                    <ArrowLeft className="h-5 w-5 text-gray-500" />
                                </button>
                                <div>
                                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                        {instance.task_flow_name}
                                    </h1>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        {instance.tenant_user_name}{instance.tenant_address ? ` — ${instance.tenant_address}` : ''}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {instance.tenant_user_role === 'pre_tenancy' && (
                                    <Button variant="outline" size="sm" onClick={handleOpenLinkModal} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                                        <Link2 className="mr-1.5 h-3.5 w-3.5" />
                                        Link to Agreement
                                    </Button>
                                )}
                                <Button variant="outline" size="sm" onClick={() => setAssignSheetOpen(true)}>
                                    <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                                    {instance.owner_admin_user_id ? 'Reassign' : 'Assign'}
                                </Button>
                                <div className="relative">
                                    <Button variant="outline" size="sm" onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}>
                                        <Badge variant={statusInfo.color} className="mr-1.5">{statusInfo.label}</Badge>
                                        <ChevronDown className="h-3.5 w-3.5" />
                                    </Button>
                                    {statusDropdownOpen && (
                                        <>
                                            <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownOpen(false)} />
                                            <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-gray-200 dark:border-zinc-700 py-1 z-20">
                                                {instance.status === 'NOT_STARTED' && (
                                                    <button onClick={() => handleStatusChange('IN_PROGRESS')} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-zinc-700 dark:text-gray-300 flex items-center gap-2">
                                                        <Play className="h-3.5 w-3.5" /> Start
                                                    </button>
                                                )}
                                                {instance.status !== 'COMPLETED' && instance.status !== 'CANCELLED' && (
                                                    <button onClick={() => handleStatusChange('CANCELLED')} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                                        <XCircle className="h-3.5 w-3.5" /> Cancel
                                                    </button>
                                                )}
                                                {(instance.status === 'COMPLETED' || instance.status === 'CANCELLED') && (
                                                    <button onClick={() => { setStatusDropdownOpen(false); handleReopenInstance(); }} className="w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2">
                                                        <RotateCcw className="h-3.5 w-3.5" /> Reopen
                                                    </button>
                                                )}
                                                <div className="border-t border-gray-100 dark:border-zinc-700 my-1" />
                                                <button onClick={() => { setStatusDropdownOpen(false); setDeleteInstanceModalOpen(true); }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Summary bar */}
                        <div className="flex items-center gap-6 mb-6 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-1.5">
                                <span className="font-medium text-gray-700 dark:text-gray-300">Assigned to:</span>
                                {instance.owner_admin_user_id ? instance.owner_admin_name : <span className="text-amber-600">Unassigned</span>}
                            </div>
                            {instance.due_date && (
                                <div className="flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span>Due {formatDate(instance.due_date)}</span>
                                </div>
                            )}
                            <div>
                                {completedCount} of {totalSteps} steps complete
                                {isInstanceDone && unfinishedCount > 0 && (
                                    <span className="text-gray-400 dark:text-gray-500"> · {unfinishedCount} not completed</span>
                                )}
                            </div>
                            <button onClick={handleViewHistory} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:underline">
                                History
                            </button>
                        </div>

                        {/* Cancellation pending approval */}
                        {instance.cancellation_pending && (
                            <div className="mb-6 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 p-4">
                                <div className="flex items-start gap-3">
                                    <Clock className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                                    <div className="text-sm flex-1">
                                        <p className="font-semibold text-amber-800 dark:text-amber-300">Cancellation requested by customer</p>
                                        <p className="text-amber-700 dark:text-amber-400 mt-1">
                                            <span className="font-medium">Reason:</span> {instance.cancellation_reason}
                                        </p>
                                        {instance.cancellation_message && (
                                            <p className="text-amber-700 dark:text-amber-400 mt-1">
                                                <span className="font-medium">Message:</span> {instance.cancellation_message}
                                            </p>
                                        )}
                                        {instance.cancelled_at && (
                                            <p className="text-amber-600/70 dark:text-amber-400/70 mt-1 text-xs">
                                                Requested {new Date(instance.cancelled_at).toLocaleString('en-GB', {
                                                    day: 'numeric', month: 'short', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit',
                                                })}
                                            </p>
                                        )}
                                        {cancellationRejectMode ? (
                                            <div className="mt-3 space-y-2">
                                                <textarea
                                                    value={cancellationRejectReason}
                                                    onChange={(e) => setCancellationRejectReason(e.target.value)}
                                                    placeholder="Message to customer (optional)"
                                                    rows={2}
                                                    className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-xs text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                                                />
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={async () => {
                                                            const res = await postAPI('admin/taskinstances/cancellation/reject', { id: instance.id, reason: cancellationRejectReason.trim() || null });
                                                            if (res.response?.ok) { toast.success('Cancellation rejected'); setCancellationRejectMode(false); setCancellationRejectReason(''); refreshData(); }
                                                            else { toast.error('Failed to reject cancellation'); }
                                                        }}
                                                        className="rounded-lg border border-gray-300 dark:border-zinc-600 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800"
                                                    >
                                                        Confirm Reject
                                                    </button>
                                                    <button
                                                        onClick={() => { setCancellationRejectMode(false); setCancellationRejectReason(''); }}
                                                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2 mt-3">
                                                <button
                                                    onClick={async () => {
                                                        const res = await postAPI('admin/taskinstances/cancellation/approve', { id: instance.id });
                                                        if (res.response?.ok) { toast.success('Cancellation approved'); refreshData(); }
                                                        else { toast.error('Failed to approve cancellation'); }
                                                    }}
                                                    className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
                                                >
                                                    Approve Cancellation
                                                </button>
                                                <button
                                                    onClick={() => setCancellationRejectMode(true)}
                                                    className="rounded-lg border border-gray-300 dark:border-zinc-600 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Cancellation details (after approved) */}
                        {isCancelled && instance.cancellation_reason && !instance.cancellation_pending && (
                            <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20 p-4">
                                <div className="flex items-start gap-3">
                                    <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                                    <div className="text-sm">
                                        <p className="font-semibold text-red-800 dark:text-red-300">Cancelled by customer</p>
                                        <p className="text-red-700 dark:text-red-400 mt-1">
                                            <span className="font-medium">Reason:</span> {instance.cancellation_reason}
                                        </p>
                                        {instance.cancellation_message && (
                                            <p className="text-red-700 dark:text-red-400 mt-1">
                                                <span className="font-medium">Message:</span> {instance.cancellation_message}
                                            </p>
                                        )}
                                        {instance.cancelled_at && (
                                            <p className="text-red-600/70 dark:text-red-400/70 mt-1 text-xs">
                                                {new Date(instance.cancelled_at).toLocaleString('en-GB', {
                                                    day: 'numeric', month: 'short', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit',
                                                })}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Two column layout */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                            {/* LEFT: Steps */}
                            <div className="lg:col-span-2 space-y-4">
                                {waitingOnStepName && (
                                    <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20 px-5 py-3 flex items-start gap-2.5">
                                        <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                                                Waiting on the customer
                                            </p>
                                            <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                                                This flow requires steps to be completed in order, so there is nothing
                                                for you to action until &ldquo;{waitingOnStepName}&rdquo; is complete.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {sortedSteps.map((step, stepIndex) => {
                                    const isAdminStep = step.actor_type_snapshot === 'admin';
                                    const isCompleted = step.status === 'COMPLETED';
                                    const isBlocked = step.status === 'BLOCKED';
                                    const isPaymentAwaitingAmount = isBlocked && step.step_type_snapshot === 'payment' && !step.step_config_snapshot?.amount;
                                    const isPendingApproval = step.status === 'PENDING_APPROVAL';
                                    // Its condition can still resolve either way, so it is neither
                                    // skipped nor outstanding — just not decided yet. Only while the
                                    // request is live: once it ends the question will never be answered,
                                    // and the step is definitively out.
                                    const isUndecided = !isInstanceDone
                                        && !step.is_conditionally_visible
                                        && !(step.is_skipped === true || step.is_skipped === 1)
                                        && isConditionUndecided(step);
                                    const isSkipped = isStepOutOfFlow(step) && !isUndecided;
                                    // Left open when the request reached a terminal state. It was never
                                    // actioned and never will be, so it reads as record rather than work.
                                    const isUnfinished = isInstanceDone && !isCompleted && !isSkipped && !isPendingApproval;
                                    // Flows with enforce_admin_step_order make the API report which admin
                                    // steps can't be worked yet. On every other flow it reports them all as
                                    // actionable, so this is inert and nothing about those flows changes.
                                    // orderEnforced is required as well as the per-step reason: an older API
                                    // computed these fields from the customer's point of view, where an admin
                                    // step behind an incomplete customer step reads as blocked. Without the
                                    // flow flag, this build pointed at that API would lock steps that are
                                    // genuinely open.
                                    const isOrderBlocked = orderEnforced
                                        && isAdminStep
                                        && step.is_actionable === false
                                        && step.blocked_reason === 'previous_step_incomplete';
                                    const isActive = !isCompleted && !isBlocked && !isSkipped && !isUndecided && !isPendingApproval && !isUnfinished && !isOrderBlocked;
                                    const fields = step.fields || [];

                                    if (isSkipped || isUnfinished || isUndecided) {
                                        const skipReason = isUndecided ? getPendingConditionReason(step)
                                            : isUnfinished ? unfinishedStepReason
                                            : getSkipReason(step);
                                        return (
                                            <Fragment key={step.id}>
                                                <div className={`rounded-xl border border-gray-200 dark:border-zinc-700 ${
                                                    isUndecided ? 'border-dashed bg-transparent' : 'bg-gray-50 dark:bg-zinc-800'
                                                }`}>
                                                    <div className="px-5 py-3 flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 text-xs font-semibold ${
                                                                isUndecided ? 'border border-dashed border-gray-300 dark:border-zinc-600' : 'bg-gray-100 dark:bg-zinc-700'
                                                            }`}>
                                                                {stepIndex + 1}
                                                            </div>
                                                            <div>
                                                                <h3 className="text-sm font-semibold text-gray-400">
                                                                    {step.name_snapshot}
                                                                </h3>
                                                                {skipReason && (
                                                                    <p className="text-xs text-gray-400 mt-0.5">{skipReason}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className="text-xs text-gray-400 font-medium">
                                                            {isUndecided ? 'Conditional' : isUnfinished ? 'Not completed' : 'Skipped'}
                                                        </span>
                                                    </div>
                                                </div>
                                                {!isInstanceCompleted && !isCancelled && (
                                                    <div className="flex justify-center -mt-2 -mb-2">
                                                        <button
                                                            onClick={() => handleAddStep(step.position)}
                                                            className="text-xs text-gray-300 hover:text-gray-500 flex items-center gap-1 transition-colors py-1"
                                                        >
                                                            <Plus className="h-3 w-3" /> Add step after this
                                                        </button>
                                                    </div>
                                                )}
                                            </Fragment>
                                        );
                                    }

                                    return (
                                        <Fragment key={step.id}>
                                        <div
                                            ref={step.id === firstActiveStepId ? currentStepRef : null}
                                            className={`scroll-mt-20 rounded-xl border transition-all ${
                                                isPendingApproval ? 'border-amber-200 bg-amber-50/30 dark:border-amber-700 dark:bg-amber-900/20' :
                                                isCompleted ? 'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800' :
                                                isPaymentAwaitingAmount ? 'border-blue-200 bg-blue-50/30 dark:border-blue-700 dark:bg-blue-900/20' :
                                                isBlocked || isOrderBlocked ? 'border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800 opacity-60' :
                                                'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800'
                                            }`}
                                        >
                                            {/* Step header */}
                                            <div className="px-5 py-4 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                                                        isPendingApproval ? 'bg-amber-100 text-amber-600' :
                                                        isCompleted ? 'bg-green-50 text-green-600' :
                                                        isPaymentAwaitingAmount ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' :
                                                        isBlocked ? 'bg-gray-100 dark:bg-zinc-700 text-gray-400' :
                                                        'bg-gray-100 dark:bg-zinc-700 text-gray-600 dark:text-gray-400'
                                                    }`}>
                                                        {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : stepIndex + 1}
                                                    </div>
                                                    <div>
                                                        <h3 className={`text-sm font-semibold ${isCompleted ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                                                            {step.name_snapshot}
                                                        </h3>
                                                        {step.description_snapshot && (
                                                            <p className="text-xs text-gray-500 mt-0.5">{step.description_snapshot}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {isPendingApproval && (
                                                        <Badge variant="warning" className="text-xs">Pending Approval</Badge>
                                                    )}
                                                    {isAdminStep && isActive && (
                                                        <Badge variant="outline" className="text-xs">Your action needed</Badge>
                                                    )}
                                                    {!isAdminStep && isActive && (
                                                        <Badge variant="outline" className="text-xs">Tenant</Badge>
                                                    )}
                                                    {isCompleted && (
                                                        <span className="text-xs text-green-600 font-medium">Completed</span>
                                                    )}
                                                    {isCompleted && !isInstanceCompleted && !isCancelled && (
                                                        <button
                                                            onClick={() => handleReopenStep(step)}
                                                            disabled={actionLoading}
                                                            className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1 transition-colors"
                                                        >
                                                            <RotateCcw className="h-3 w-3" />
                                                            Reopen
                                                        </button>
                                                    )}
                                                    {isPaymentAwaitingAmount && (
                                                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Amount required</span>
                                                    )}
                                                    {isBlocked && !isPaymentAwaitingAmount && (
                                                        <span className="text-xs text-gray-400 font-medium">Upcoming</span>
                                                    )}
                                                    {isOrderBlocked && !isBlocked && (
                                                        <span className="text-xs text-gray-400 font-medium">Waiting</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Held by step order — say what opens it, or the greyed-out
                                                card reads as a broken page. */}
                                            {isOrderBlocked && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-3">
                                                    <div className="flex items-start gap-2">
                                                        <Lock className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                                                        <p className="text-xs text-gray-500">
                                                            {step.blocked_by_step_name
                                                                ? <>Opens once <span className="font-medium">&ldquo;{step.blocked_by_step_name}&rdquo;</span> is complete. This flow requires steps to be completed in order.</>
                                                                : 'Not available yet. This flow requires steps to be completed in order.'}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Step content */}
                                            {fields.length > 0 && !isOrderBlocked && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                    {/* Completed step: show submitted values */}
                                                    {isCompleted && (
                                                        <div className="space-y-3">
                                                            {fields.sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)).map((field) => (
                                                                <div key={field.id} className="flex items-start gap-3">
                                                                    <div className="w-1/3 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">{field.name_snapshot}</span>
                                                                    </div>
                                                                    <div className="flex-1 min-w-0 break-words">
                                                                        <span className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{renderFieldValue(field)}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Pending approval step: show submitted values + approve/reject */}
                                                    {isPendingApproval && (
                                                        <div className="space-y-3">
                                                            {fields.sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)).map((field) => (
                                                                <div key={field.id} className="flex items-start gap-3">
                                                                    <div className="w-1/3 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">{field.name_snapshot}</span>
                                                                    </div>
                                                                    <div className="flex-1 min-w-0 break-words">
                                                                        <span className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{renderFieldValue(field)}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            <div className="flex items-center gap-2 pt-3 border-t border-amber-100">
                                                                <Button
                                                                    onClick={() => handleApproveStep(step)}
                                                                    disabled={actionLoading}
                                                                    className="bg-green-600 hover:bg-green-700 text-white"
                                                                    size="sm"
                                                                >
                                                                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleRejectStep(step)}
                                                                    disabled={actionLoading}
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                                                >
                                                                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                                                                    Reject
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Active tenant step: show what they've submitted so far */}
                                                    {isActive && !isAdminStep && (
                                                        <div className="space-y-3">
                                                            {fields.sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)).map((field) => {
                                                                const hasValue = renderFieldValue(field) !== '-';
                                                                return (
                                                                    <div key={field.id} className="flex items-start gap-3">
                                                                        <div className="w-1/3 min-w-0">
                                                                            <span className="text-xs font-medium text-gray-500 inline-flex items-center gap-1">
                                                                                {field.name_snapshot}
                                                                                {field.is_required_snapshot && <span className="text-red-400 ml-0.5">*</span>}
                                                                                {field.help_url_snapshot && (
                                                                                    <a href={field.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                                                                                        <ExternalLink className="h-3 w-3" />
                                                                                    </a>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex-1 min-w-0 break-words">
                                                                            {hasValue ? (
                                                                                <span className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{renderFieldValue(field)}</span>
                                                                            ) : (
                                                                                <span className="text-sm text-gray-300 italic">Awaiting response</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* Active admin step: show editable form */}
                                                    {isActive && isAdminStep && (
                                                        <div className="space-y-4">
                                                            {fields.sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)).map((field) => (
                                                                <div key={field.id}>
                                                                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                                                        {field.name_snapshot}
                                                                        {field.is_required_snapshot && <span className="text-red-500 ml-0.5">*</span>}
                                                                        {field.help_url_snapshot && (
                                                                            <a href={field.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                                                                                <ExternalLink className="h-3.5 w-3.5" />
                                                                            </a>
                                                                        )}
                                                                    </label>
                                                                    {field.help_text_snapshot && (
                                                                        <p className="text-xs text-gray-400 mb-1.5">{field.help_text_snapshot}</p>
                                                                    )}
                                                                    {renderAdminFieldInput(field)}
                                                                </div>
                                                            ))}
                                                            <div className="pt-2">
                                                                <Button
                                                                    onClick={() => handleAdminSubmitStep(step)}
                                                                    disabled={submittingStep === step.id}
                                                                    className=""
                                                                >
                                                                    {submittingStep === step.id ? 'Submitting...' : 'Submit'}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Approve/reject buttons for pending approval steps with no fields */}
                                            {isPendingApproval && fields.length === 0 && (
                                                <div className="px-5 pb-4">
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            onClick={() => handleApproveStep(step)}
                                                            disabled={actionLoading}
                                                            className="bg-green-600 hover:bg-green-700 text-white"
                                                            size="sm"
                                                        >
                                                            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleRejectStep(step)}
                                                            disabled={actionLoading}
                                                            variant="outline"
                                                            size="sm"
                                                            className="border-red-300 text-red-600 hover:bg-red-50"
                                                        >
                                                            <XCircle className="mr-1.5 h-3.5 w-3.5" />
                                                            Reject
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Payment / upload info for special steps */}
                                            {step.step_type_snapshot === 'payment' && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <CreditCard className="h-4 w-4 text-blue-600" />
                                                        {isCompleted ? (
                                                            <span className="text-gray-600 dark:text-gray-400">Payment: £{step.step_config_snapshot?.amount || '—'}</span>
                                                        ) : step.step_config_snapshot?.amount && editingPaymentStepId !== step.id ? (
                                                            <span className="text-gray-600 dark:text-gray-400">
                                                                Payment: £{step.step_config_snapshot.amount}
                                                                {!isInstanceCompleted && !isCancelled && (
                                                                    <button
                                                                        onClick={() => { setEditingPaymentStepId(step.id); setPaymentAmountInput(step.step_config_snapshot.amount); }}
                                                                        className="ml-2 text-blue-600 hover:text-blue-800 text-xs underline"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="text-amber-600 dark:text-amber-400">Amount not yet set</span>
                                                        )}
                                                        {step.step_result?.status && (
                                                            <Badge variant="outline" className="text-xs">{step.step_result.status}</Badge>
                                                        )}
                                                    </div>
                                                    {!isCompleted && !isInstanceCompleted && !isCancelled && (
                                                        <>
                                                            {(editingPaymentStepId === step.id || !step.step_config_snapshot?.amount) && (
                                                                <div className="mt-3 flex items-center gap-2">
                                                                    <div className="relative">
                                                                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">£</span>
                                                                        <input
                                                                            type="number"
                                                                            min="0.01"
                                                                            step="0.01"
                                                                            value={editingPaymentStepId === step.id ? paymentAmountInput : ''}
                                                                            onFocus={() => { if (editingPaymentStepId !== step.id) { setEditingPaymentStepId(step.id); setPaymentAmountInput(''); } }}
                                                                            onChange={(e) => { setEditingPaymentStepId(step.id); setPaymentAmountInput(e.target.value); }}
                                                                            placeholder="0.00"
                                                                            className="pl-6 w-32 rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                                                        />
                                                                    </div>
                                                                    <Button
                                                                        onClick={() => handleSetPaymentAmount(step.id)}
                                                                        disabled={actionLoading}
                                                                        size="sm"
                                                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                                                    >
                                                                        {step.step_config_snapshot?.amount ? 'Update Amount' : 'Set Amount'}
                                                                    </Button>
                                                                    {editingPaymentStepId === step.id && step.step_config_snapshot?.amount && (
                                                                        <button
                                                                            onClick={() => { setEditingPaymentStepId(null); setPaymentAmountInput(''); }}
                                                                            className="text-xs text-gray-500 hover:text-gray-700"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {step.step_config_snapshot?.amount && editingPaymentStepId !== step.id && !isOrderBlocked && (
                                                                <div className="mt-3">
                                                                    <Button
                                                                        onClick={() => handleStepStatusChange(step.id, 'COMPLETED')}
                                                                        disabled={actionLoading}
                                                                        size="sm"
                                                                        className="bg-green-600 hover:bg-green-700 text-white"
                                                                    >
                                                                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                                        Mark Payment Complete
                                                                    </Button>
                                                                    <p className="text-xs text-gray-400 mt-1.5">Use this if the tenant paid outside of the portal</p>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            {step.step_type_snapshot === 'document_upload' && (() => {
                                                const files = Object.values(step.step_result?.files || {});
                                                const folderUrl = step.step_result?.folder_url || null;
                                                // Admins upload on their own steps only. Tenant-actor steps stay
                                                // read-only here; a completed step must be reopened first.
                                                const canUpload = isAdminStep && isActive && !isInstanceCompleted && !isCancelled;
                                                if (files.length === 0 && !canUpload) return null;

                                                const uploadConfig = step.step_config_snapshot || {};
                                                const pickedFiles = uploadPickedFiles[step.id] || [];
                                                const stepUploadState = uploadStepState[step.id];
                                                const isUploading = stepUploadState === 'uploading';
                                                const progressForStep = uploadFileProgress[step.id] || {};

                                                return (
                                                    <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                        {files.length > 0 && (
                                                            <div className="space-y-1">
                                                                {files.map((file, idx) => (
                                                                    <div key={idx} className="flex items-center gap-2 text-sm">
                                                                        <FileText className="h-3.5 w-3.5 text-gray-400" />
                                                                        {file.web_url ? (
                                                                            <a href={file.web_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                                                                                {file.filename}
                                                                            </a>
                                                                        ) : (
                                                                            <span className="text-gray-700 dark:text-gray-300">{file.filename}</span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {folderUrl && (
                                                            <a href={folderUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mt-3">
                                                                <FolderOpen className="h-3.5 w-3.5" />
                                                                Open folder in SharePoint
                                                                <ExternalLink className="h-3 w-3" />
                                                            </a>
                                                        )}

                                                        {canUpload && (
                                                            <div className={files.length > 0 || folderUrl ? 'mt-4' : ''}>
                                                                {step.help_text_snapshot && (
                                                                    <p className="text-xs text-gray-400 mb-2">{step.help_text_snapshot}</p>
                                                                )}

                                                                {!isUploading && (
                                                                    <div
                                                                        onDragOver={(e) => { e.preventDefault(); setUploadDragOverStepId(step.id); }}
                                                                        onDragLeave={(e) => { e.preventDefault(); setUploadDragOverStepId(null); }}
                                                                        onDrop={(e) => { e.preventDefault(); setUploadDragOverStepId(null); handleUploadFileSelect(step, e.dataTransfer.files); }}
                                                                        onClick={() => uploadInputRefs.current[step.id]?.click()}
                                                                        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                                                                            uploadDragOverStepId === step.id
                                                                                ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-900/20'
                                                                                : 'border-gray-200 dark:border-zinc-600 bg-gray-50 dark:bg-zinc-900/40 hover:border-gray-300 dark:hover:border-zinc-500'
                                                                        }`}
                                                                    >
                                                                        <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                            Click to upload or drag and drop
                                                                        </p>
                                                                        <p className="text-xs text-gray-400 mt-1">
                                                                            {uploadConfig.allowed_types?.map(t => t.toUpperCase()).join(', ') || 'Any file type'} up to {uploadConfig.max_size_mb || 20}MB
                                                                            {' · '}max {uploadConfig.max_files || 5} files
                                                                        </p>
                                                                        <input
                                                                            ref={(el) => { uploadInputRefs.current[step.id] = el; }}
                                                                            type="file"
                                                                            multiple
                                                                            onChange={(e) => { handleUploadFileSelect(step, e.target.files); e.target.value = ''; }}
                                                                            className="hidden"
                                                                            accept={uploadConfig.allowed_types?.map(t => '.' + t).join(',') || undefined}
                                                                        />
                                                                    </div>
                                                                )}

                                                                {pickedFiles.length > 0 && (
                                                                    <div className="mt-3 space-y-2">
                                                                        {pickedFiles.map((file, idx) => {
                                                                            const fileProgress = progressForStep[idx];
                                                                            return (
                                                                                <div key={idx} className="flex items-center gap-3 rounded-lg bg-gray-50 dark:bg-zinc-900/40 px-3 py-2">
                                                                                    <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{file.name}</p>
                                                                                        <p className="text-xs text-gray-400">
                                                                                            {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)}KB` : `${(file.size / 1024 / 1024).toFixed(1)}MB`}
                                                                                            {fileProgress?.status === 'uploading' && fileProgress.progress > 0 && ` · ${fileProgress.progress}%`}
                                                                                            {fileProgress?.error && ` · ${fileProgress.error}`}
                                                                                        </p>
                                                                                    </div>
                                                                                    {!fileProgress && !isUploading && (
                                                                                        <button
                                                                                            onClick={(e) => { e.stopPropagation(); removeUploadPickedFile(step.id, idx); }}
                                                                                            className="text-gray-400 hover:text-gray-600 shrink-0"
                                                                                        >
                                                                                            <X className="h-4 w-4" />
                                                                                        </button>
                                                                                    )}
                                                                                    {fileProgress?.status === 'uploading' && (
                                                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 shrink-0"></div>
                                                                                    )}
                                                                                    {fileProgress?.status === 'scanning' && (
                                                                                        <div className="flex items-center gap-1.5 shrink-0">
                                                                                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-400"></div>
                                                                                            <span className="text-xs text-gray-400">Scanning</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {fileProgress?.status === 'done' && (
                                                                                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                                                                                    )}
                                                                                    {(fileProgress?.status === 'error' || fileProgress?.status === 'rejected') && (
                                                                                        <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}

                                                                <div className="mt-3">
                                                                    <Button
                                                                        onClick={() => handleAdminUploadAndComplete(step)}
                                                                        disabled={isUploading || pickedFiles.length === 0}
                                                                    >
                                                                        <Upload className="mr-1.5 h-3.5 w-3.5" />
                                                                        {isUploading
                                                                            ? 'Uploading...'
                                                                            : `Upload & Complete${pickedFiles.length > 0 ? ` (${pickedFiles.length})` : ''}`}
                                                                    </Button>
                                                                    <p className="text-xs text-gray-400 mt-1.5">
                                                                        Files are virus scanned and saved to SharePoint, then this step is marked complete.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            {step.step_type_snapshot === 'document_signing' && step.step_config_snapshot && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                    <div className="space-y-1 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <PenTool className="h-4 w-4 text-amber-600" />
                                                            <span className="text-gray-600 dark:text-gray-400">Template: {step.step_config_snapshot.template_name}</span>
                                                            {step.step_result?.status && (
                                                                <Badge variant="outline" className="text-xs capitalize">{step.step_result.status}</Badge>
                                                            )}
                                                        </div>
                                                        {step.step_config_snapshot.agreement_name && (
                                                            <div className="flex items-center gap-2 pl-6">
                                                                <span className="text-gray-600 dark:text-gray-400">Agreement: {resolveAgreementName(step.step_config_snapshot)}</span>
                                                            </div>
                                                        )}
                                                        {/* Signing order indicator */}
                                                        {step.step_config_snapshot.require_admin_countersign && (
                                                            <div className="flex items-center gap-1.5 pl-6 mt-1">
                                                                <Users className="h-3.5 w-3.5 text-gray-400" />
                                                                <span className="text-xs text-gray-500">
                                                                    Signing order: {(step.step_config_snapshot.signing_order || 'customer_first') === 'admin_first' ? (
                                                                        <span className="text-gray-700 dark:text-gray-300">Admin <ArrowRight className="inline h-3 w-3 text-gray-400" /> Customer</span>
                                                                    ) : (
                                                                        <span className="text-gray-700 dark:text-gray-300">Customer <ArrowRight className="inline h-3 w-3 text-gray-400" /> Admin</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {step.step_config_snapshot.require_admin_countersign && (
                                                            <div className="flex items-center gap-1.5 pl-6">
                                                                <PenTool className="h-3.5 w-3.5 text-gray-400" />
                                                                <span className="text-xs text-gray-500">
                                                                    Counter-signer: <span className="text-gray-700 dark:text-gray-300">
                                                                        {resolvedCountersignerLabel(step.step_result?.countersigner)
                                                                            || countersignerSummary(countersignerFromInstanceStep(step), adminUsers)}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Not yet initiated or cancelled — allow creating draft */}
                                                    {(!step.step_result?.agreement_id || step.step_result?.status === 'cancelled') && !isCompleted && !isInstanceCompleted && !isCancelled && !isOrderBlocked && (
                                                        <div className="mt-3">
                                                            <Button
                                                                onClick={() => handleInitiateDocumentSigning(step)}
                                                                disabled={actionLoading}
                                                                size="sm"
                                                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                                            >
                                                                <PenTool className="mr-1.5 h-3.5 w-3.5" />
                                                                Create Draft
                                                            </Button>
                                                        </div>
                                                    )}

                                                    {/* Draft — review and send */}
                                                    {step.step_result?.agreement_id && step.step_result?.status === 'draft' && !isCompleted && !isInstanceCompleted && !isCancelled && (
                                                        <div className="mt-3 space-y-2">
                                                            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                                                                <p className="text-xs font-medium text-amber-700 flex-1">Draft agreement created. Review the document and send when ready.</p>
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <HelpCircle className="h-3.5 w-3.5 text-amber-400 cursor-help shrink-0 mt-0.5" />
                                                                        </TooltipTrigger>
                                                                        <TooltipContent className="max-w-xs">
                                                                            <p className="font-medium mb-1">Preview Document</p>
                                                                            <p className="text-xs mb-2">Opens a PDF with all pre-filled field values visible, so you can verify the document content before sending.</p>
                                                                            <p className="font-medium mb-1">Edit in Adobe Sign</p>
                                                                            <p className="text-xs">Opens the draft in the Adobe Sign console where you can edit fields and layout. Pre-filled values are not shown in this view — they are applied when the document is sent for signing.</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <Button
                                                                    onClick={() => handleReviewDraft(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    variant="outline"
                                                                >
                                                                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                                                    Preview Document
                                                                </Button>
                                                                {step.step_result?.preview_url && (
                                                                    <Button
                                                                        onClick={() => window.open(step.step_result.preview_url, '_blank')}
                                                                        disabled={actionLoading}
                                                                        size="sm"
                                                                        variant="outline"
                                                                    >
                                                                        <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                                                        Edit in Adobe Sign
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    onClick={() => handleSendForSigning(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    className="bg-amber-600 hover:bg-amber-700 text-white"
                                                                >
                                                                    <PenTool className="mr-1.5 h-3.5 w-3.5" />
                                                                    Send for Signing
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleCancelDocumentSigning(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                                                >
                                                                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                                                                    Discard Draft
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Signing in progress */}
                                                    {step.step_result?.agreement_id && step.step_result?.status === 'sent' && !isCompleted && (
                                                        <div className="mt-3 space-y-2">
                                                            {/* Multi-participant progress */}
                                                            {step.step_result?.participants && (
                                                                <div className="rounded-md border border-gray-200 dark:border-zinc-700 divide-y divide-gray-100 dark:divide-zinc-700 mb-2">
                                                                    {step.step_result.participants
                                                                        .sort((a, b) => a.order - b.order)
                                                                        .map((p, idx) => (
                                                                        <div key={idx} className="flex items-center gap-3 px-3 py-2">
                                                                            <span className="text-xs text-gray-400 font-mono w-4 text-center">{p.order}</span>
                                                                            {p.status === 'signed' ? (
                                                                                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                                                                            ) : p.status === 'pending' ? (
                                                                                <PenTool className="h-4 w-4 text-amber-500 shrink-0" />
                                                                            ) : (
                                                                                <Clock className="h-4 w-4 text-gray-300 shrink-0" />
                                                                            )}
                                                                            <div className="flex-1 min-w-0">
                                                                                <span className={`text-sm ${p.status === 'signed' ? 'text-green-700' : p.status === 'pending' ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                                                                                    {p.role === 'customer' ? 'Customer' : p.role === 'joint_customer' ? 'Joint Signer' : 'Admin'}
                                                                                </span>
                                                                                {p.email && <span className="text-xs text-gray-400 ml-1.5">{p.email}</span>}
                                                                            </div>
                                                                            <Badge variant="outline" className={`text-xs shrink-0 ${p.status === 'signed' ? 'border-green-200 text-green-600' : p.status === 'pending' ? 'border-amber-200 text-amber-600' : 'border-gray-200 text-gray-400'}`}>
                                                                                {p.status === 'signed' ? 'Signed' : p.status === 'pending' ? 'Awaiting signature' : 'Up next'}
                                                                            </Badge>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <div className="flex items-center gap-2">
                                                                {/* Sign Document is only for the nominated counter-signer — anyone
                                                                    else sees who the document is waiting on. */}
                                                                {step.step_result?.current_signer === 'admin' && step.can_countersign !== false && (
                                                                    <Button
                                                                        onClick={() => handleAdminSign(step.id)}
                                                                        disabled={actionLoading}
                                                                        size="sm"
                                                                        className="bg-amber-600 hover:bg-amber-700 text-white"
                                                                    >
                                                                        <PenTool className="mr-1.5 h-3.5 w-3.5" />
                                                                        Sign Document
                                                                    </Button>
                                                                )}
                                                                {step.step_result?.current_signer === 'admin' && step.can_countersign === false && (
                                                                    <span className="text-xs text-amber-700 dark:text-amber-400">
                                                                        Awaiting counter-signature from {resolvedCountersignerLabel(step.step_result?.countersigner)
                                                                            || 'the nominated counter-signer'}
                                                                    </span>
                                                                )}
                                                                <Button
                                                                    onClick={() => handleCheckSigningStatus(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    variant="outline"
                                                                >
                                                                    Check Status
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleCancelDocumentSigning(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                                                >
                                                                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                                                                    Cancel Signing
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Signed */}
                                                    {step.step_result?.status === 'signed' && (
                                                        <p className="text-xs text-green-600 mt-2">
                                                            Signed on {new Date(step.step_result.signed_at).toLocaleDateString()}
                                                        </p>
                                                    )}

                                                    {/* Admin manual complete — only after document has been sent for signing */}
                                                    {step.step_result?.agreement_id && step.step_result?.status === 'sent' && !isCompleted && !isInstanceCompleted && !isCancelled && !isOrderBlocked && (
                                                        <div className="mt-3 border-t border-gray-100 dark:border-zinc-700 pt-3">
                                                            <Button
                                                                onClick={() => handleStepStatusChange(step.id, 'COMPLETED')}
                                                                disabled={actionLoading}
                                                                size="sm"
                                                                variant="outline"
                                                                className="border-green-300 text-green-600 hover:bg-green-50"
                                                            >
                                                                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                                Mark as Signed (Manual)
                                                            </Button>
                                                            <p className="text-xs text-gray-400 mt-1.5">Use if the document was signed outside the portal</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {step.step_type_snapshot === 'info' && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Info className="h-4 w-4 text-blue-600" />
                                                        <span className="text-gray-600 dark:text-gray-400">Information / acknowledgement step</span>
                                                    </div>
                                                    {step.description_snapshot && (
                                                        <p className="text-sm text-gray-500 mt-2">{step.description_snapshot}</p>
                                                    )}
                                                    {isAdminStep && isActive && (
                                                        <div className="pt-3">
                                                            <Button
                                                                onClick={() => handleStepStatusChange(step.id, 'COMPLETED')}
                                                                disabled={actionLoading}
                                                            >
                                                                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                                Acknowledge
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Add field button (not for document_signing or info steps) */}
                                            {(isActive || isPendingApproval) && step.step_type_snapshot === 'standard' && (
                                                <div className="px-5 pb-4">
                                                    <button
                                                        onClick={() => handleAddField(step.id)}
                                                        className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
                                                    >
                                                        <Plus className="h-3 w-3" /> Add field
                                                    </button>
                                                </div>
                                            )}

                                            {/* Per-step action (webhook) activity */}
                                            <StepActionActivity deliveries={deliveriesByStep[step.id] || []} onRetry={handleRetryDelivery} />
                                        </div>
                                        {!isInstanceCompleted && !isCancelled && (
                                            <div className="flex justify-center -mt-2 -mb-2">
                                                <button
                                                    onClick={() => handleAddStep(step.position)}
                                                    className="text-xs text-gray-300 hover:text-gray-500 flex items-center gap-1 transition-colors py-1"
                                                >
                                                    <Plus className="h-3 w-3" /> Add step after this
                                                </button>
                                            </div>
                                        )}
                                        </Fragment>
                                    );
                                })}

                                {sortedSteps.length === 0 && (
                                    <div className="text-center py-12 text-sm text-gray-500">
                                        No steps found for this request.
                                    </div>
                                )}

                                {/* Outcome Card */}
                                {instance && !isCancelled && (
                                    <div className={`rounded-xl border transition-all ${
                                        isInstanceCompleted ? 'border-green-200 bg-white dark:bg-zinc-800 dark:border-green-800' :
                                        allVisibleStepsComplete ? 'border-amber-200 bg-white dark:bg-zinc-800 dark:border-amber-700' :
                                        'border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800'
                                    }`}>
                                        {/* Completed state */}
                                        {isInstanceCompleted && (
                                            <div className="px-5 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
                                                            <CheckCircle2 className="h-4 w-4" />
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Outcome</h3>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                                                                {instance.completion_outcome === 'positive' ? 'Request completed' : 'Request closed'}
                                                                {instance.completion_message && <>: "{instance.completion_message}"</>}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {isAutoCompleted && (
                                                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                                                <Zap className="mr-1 h-3 w-3" />
                                                                Auto-completed
                                                            </Badge>
                                                        )}
                                                        <span className="text-xs text-green-600 font-medium">Completed</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Active state - all steps done, ready for outcome */}
                                        {!isInstanceCompleted && allVisibleStepsComplete && (
                                            <div className="px-5 py-4">
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                                                        <Circle className="h-4 w-4" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Outcome</h3>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">All steps have been completed. Choose an outcome for this request.</p>
                                                    </div>
                                                </div>

                                                <div className="flex gap-3 mb-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedOutcome('positive');
                                                            setOutcomeMessage(instance.positive_outcome_message || instance.default_completion_message || 'Your request has been completed.');
                                                        }}
                                                        className={`flex-1 text-left rounded-lg border-2 p-3 transition-colors ${
                                                            selectedOutcome === 'positive'
                                                                ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                                                                : 'border-gray-200 dark:border-zinc-700 hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-900/20'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                                                            <div>
                                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Complete Request</p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedOutcome('negative');
                                                            setOutcomeMessage(instance.negative_outcome_message || 'Your request has been closed.');
                                                        }}
                                                        className={`flex-1 text-left rounded-lg border-2 p-3 transition-colors ${
                                                            selectedOutcome === 'negative'
                                                                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30'
                                                                : 'border-gray-200 dark:border-zinc-700 hover:border-amber-300 hover:bg-amber-50/50 dark:hover:bg-amber-900/20'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <XCircle className="h-5 w-5 text-amber-600 shrink-0" />
                                                            <div>
                                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Close Request</p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                </div>

                                                {selectedOutcome && (
                                                    <>
                                                        <div className="mb-3">
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                Message to customer
                                                            </label>
                                                            <textarea
                                                                value={outcomeMessage}
                                                                onChange={(e) => setOutcomeMessage(e.target.value)}
                                                                rows={3}
                                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6 dark:bg-zinc-800"
                                                            />
                                                        </div>
                                                        <div className="flex justify-end">
                                                            <Button
                                                                onClick={handleOutcomeConfirm}
                                                                disabled={outcomeLoading}
                                                            >
                                                                {outcomeLoading ? 'Completing...' : 'Confirm'}
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Upcoming state - steps still incomplete */}
                                        {!isInstanceCompleted && !allVisibleStepsComplete && (
                                            <div className="px-5 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-700 text-gray-400">
                                                            <Circle className="h-4 w-4" />
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-gray-400">Outcome</h3>
                                                            <p className="text-xs text-gray-400">Complete all steps before choosing outcome</p>
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-gray-400 font-medium">Upcoming</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* RIGHT: Feedback + Messages */}
                            {/* Drop the sticky offset below the floating pill/actions once scrolled so it isn't hidden underneath. */}
                            <div className={`lg:col-span-1 space-y-4 lg:sticky lg:self-start transition-[top] duration-300 ${scrolled ? 'lg:top-20' : 'lg:top-4'}`}>
                                {/* Feedback card */}
                                {instance?.feedback && (
                                    <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 px-4 py-3">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Tenant Feedback</h3>
                                        </div>
                                        <div className="flex items-center gap-1 mb-1">
                                            {[1, 2, 3, 4, 5].map(i => (
                                                <Star
                                                    key={i}
                                                    className={`h-4 w-4 ${i <= instance.feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                                                />
                                            ))}
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">{instance.feedback.rating}/5</span>
                                        </div>
                                        {instance.feedback.comment && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400 italic mt-1">{instance.feedback.comment}</p>
                                        )}
                                    </div>
                                )}

                                <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 flex flex-col" style={{ height: instance?.feedback ? 'calc(100vh - 300px)' : 'calc(100vh - 220px)' }}>
                                    {/* Messages header */}
                                    <div className="px-4 py-3 border-b border-gray-100 dark:border-zinc-700 flex items-center gap-2">
                                        <MessageSquare className="h-4 w-4 text-gray-400" />
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Messages</h3>
                                        {messages.filter(m => !m.is_read_by_admin).length > 0 && (
                                            <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                                {messages.filter(m => !m.is_read_by_admin).length}
                                            </span>
                                        )}
                                    </div>

                                    {/* Messages list */}
                                    <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col-reverse gap-3">
                                        {messages.length === 0 ? (
                                            <div className="text-center py-8">
                                                <MessageSquare className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                                                <p className="text-sm text-gray-400">No messages yet</p>
                                            </div>
                                        ) : (
                                            [...messages].reverse().map((msg) => {
                                                const isAdmin = msg.sender_role === 'admin';
                                                return (
                                                    <div
                                                        key={msg.id}
                                                        className={`group relative rounded-lg px-3 py-2.5 text-sm ${
                                                            isAdmin
                                                                ? 'bg-gray-100 dark:bg-zinc-700 ml-6'
                                                                : 'bg-gray-50 dark:bg-zinc-700/50 mr-6 dark:ring-1 dark:ring-zinc-600'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className={`text-xs font-medium ${isAdmin ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>
                                                                {isAdmin ? 'You' : msg.sender_name || 'Tenant'}
                                                            </span>
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs text-gray-400">
                                                                    {new Date(msg.created_at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                                <button
                                                                    onClick={() => handleDeleteMessage(msg.id)}
                                                                    className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-300 hover:text-red-500 transition-all"
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {msg.subject && (
                                                            <p className="font-medium text-gray-900 dark:text-white text-sm">{msg.subject}</p>
                                                        )}
                                                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{msg.body}</p>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* Compose */}
                                    {['COMPLETED', 'CANCELLED'].includes(instance?.status) ? (
                                        <div className="border-t border-gray-100 dark:border-zinc-700 px-3 py-3">
                                            <p className="text-xs text-gray-400 text-center">
                                                This request is {instance.status === 'CANCELLED' ? 'cancelled' : 'complete'}. Messaging is closed.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="border-t border-gray-100 dark:border-zinc-700 px-3 py-3">
                                            <div className="flex items-end gap-2">
                                                <textarea
                                                    ref={messageInputRef}
                                                    placeholder="Type a message..."
                                                    value={newMessageBody}
                                                    onChange={(e) => setNewMessageBody(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSendMessage();
                                                        }
                                                    }}
                                                    rows={1}
                                                    style={{maxHeight: '160px'}}
                                                    className="flex-1 text-sm border border-gray-200 dark:border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none overflow-y-auto dark:bg-zinc-800 dark:text-white"
                                                />
                                                <Button
                                                    size="icon"
                                                    onClick={handleSendMessage}
                                                    disabled={!newMessageBody.trim() || sendingMessage}
                                                    className="shrink-0 bg-gray-900 hover:bg-gray-800"
                                                >
                                                    <Send className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                )}

                {/* Assignment Sheet */}
                <Sheet open={assignSheetOpen} onOpenChange={setAssignSheetOpen}>
                    <SheetContent>
                        <SheetHeader>
                            <SheetTitle>Assign Task Instance</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                            {instance && (
                                <AssignTaskInstanceForm
                                    instanceId={instance.id}
                                    setVisible={setAssignSheetOpen}
                                    onAssigned={refreshData}
                                />
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Add Field Sheet */}
                <Sheet open={addFieldSheetOpen} onOpenChange={setAddFieldSheetOpen}>
                    <SheetContent>
                        <SheetHeader>
                            <SheetTitle>Add Field</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                            {selectedStepId && (
                                <AddInstanceFieldForm
                                    stepId={selectedStepId}
                                    setVisible={setAddFieldSheetOpen}
                                    onFieldAdded={refreshData}
                                />
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Assignment History Sheet */}
                <Sheet open={historySheetOpen} onOpenChange={setHistorySheetOpen}>
                    <SheetContent>
                        <SheetHeader>
                            <SheetTitle>Assignment History</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6 space-y-4">
                            {assignmentHistory.length > 0 ? (
                                assignmentHistory.map((entry, idx) => (
                                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-zinc-800 rounded-md border border-gray-200 dark:border-zinc-700">
                                        <Clock className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 dark:text-white">
                                                {entry.previous_owner_name
                                                    ? `Reassigned from ${entry.previous_owner_name} to ${entry.new_owner_name || 'Unassigned'}`
                                                    : `Assigned to ${entry.new_owner_name || 'Unassigned'}`
                                                }
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">by {entry.changed_by_name}</p>
                                            {entry.note && (
                                                <p className="text-xs text-gray-500 mt-1">{entry.note}</p>
                                            )}
                                            <p className="text-xs text-gray-400 mt-1">{formatDateTime(entry.changed_at)}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500">No assignment history.</p>
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Add Step Sheet */}
                <Sheet open={addStepSheetOpen} onOpenChange={setAddStepSheetOpen}>
                    <SheetContent>
                        <SheetHeader>
                            <SheetTitle>Add Step</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                            {instance && (
                                <AddInstanceStepForm
                                    instanceId={instance.id}
                                    insertAfterPosition={addStepAfterPosition}
                                    setVisible={setAddStepSheetOpen}
                                    onStepAdded={refreshData}
                                />
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Completion Message Modal */}
                <CompletionMessageModal
                    open={completionModalOpen}
                    setOpen={setCompletionModalOpen}
                    defaultMessage={instance?.default_completion_message || ''}
                    onConfirm={(outcome, message) => pendingLastStep ? handleCompleteLastStep(outcome, message) : handleStatusCompleteWithOutcome(outcome, message)}
                    loading={completionModalLoading}
                />

                {/* Review Step Modal */}
                <ReviewStepModal
                    open={reviewModalOpen}
                    setOpen={setReviewModalOpen}
                    defaultCloseMessage={instance?.negative_outcome_message || 'Your request has been closed.'}
                    onConfirm={handleReviewConfirm}
                    loading={reviewLoading}
                />

                {/* Reopen Step Modal */}
                <ReopenStepModal
                    open={reopenModalOpen}
                    setOpen={setReopenModalOpen}
                    stepName={reopenStep?.name_snapshot || ''}
                    isAdminStep={reopenStep?.actor_type_snapshot === 'admin'}
                    reason={reopenReason}
                    setReason={setReopenReason}
                    onConfirm={handleReopenConfirm}
                    loading={reopenLoading}
                />

                {/* Reopen Task Instance Modal */}
                <ReopenTaskInstanceModal
                    open={reopenInstanceModalOpen}
                    setOpen={setReopenInstanceModalOpen}
                    reason={reopenInstanceReason}
                    setReason={setReopenInstanceReason}
                    onConfirm={handleReopenInstanceConfirm}
                    loading={reopenInstanceLoading}
                />

                {/* Link to Agreement Modal */}
                <LinkToAgreementModal
                    open={linkModalOpen}
                    setOpen={setLinkModalOpen}
                    fields={linkableFields}
                    tenantUser={linkTenantUser}
                    onConfirm={handleLinkConfirm}
                    loading={linkLoading}
                    fieldsLoading={linkFieldsLoading}
                />

                {/* Delete Field Modal */}
                {/* Send for Signing Confirmation Modal */}
                <Transition.Root show={signingConfirmModalOpen} as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={setSigningConfirmModalOpen}>
                        <Transition.Child as={Fragment} enter="ease-out duration-100" enterFrom="opacity-0" enterTo="opacity-90" leave="ease-in duration-100" leaveFrom="opacity-90" leaveTo="opacity-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>
                        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
                                <Transition.Child as={Fragment} enter="ease-out duration-100" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                        <div>
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                                                    <PenTool className="h-5 w-5 text-amber-600" />
                                                </div>
                                                <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                                                    Create Draft Agreement
                                                </Dialog.Title>
                                            </div>

                                            {signingConfirmStep?.step_config_snapshot && (
                                                <div className="space-y-4">
                                                    <div className="rounded-md bg-gray-50 dark:bg-zinc-700 px-3 py-2">
                                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Template</p>
                                                        <p className="text-sm text-gray-900 dark:text-white mt-0.5">{signingConfirmStep.step_config_snapshot.template_name}</p>
                                                    </div>
                                                    <div className="rounded-md bg-gray-50 dark:bg-zinc-700 px-3 py-2">
                                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Agreement Name</p>
                                                        <p className="text-sm text-gray-900 dark:text-white mt-0.5">{resolveAgreementName(signingConfirmStep.step_config_snapshot)}</p>
                                                    </div>
                                                    {signingConfirmStep.step_config_snapshot.require_admin_countersign && (() => {
                                                        const isCustomerFirst = (signingConfirmStep.step_config_snapshot.signing_order || 'customer_first') === 'customer_first';
                                                        const countersignerLabel = countersignerSummary(countersignerOverride, adminUsers);
                                                        return (
                                                            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                                                                <p className="text-xs font-medium text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
                                                                    <Users className="h-3.5 w-3.5" />
                                                                    Signing Order
                                                                </p>
                                                                <div className="flex items-center gap-2 mt-1.5">
                                                                    <span className={`text-sm font-medium ${isCustomerFirst ? 'text-amber-800' : 'text-gray-500'}`}>
                                                                        {isCustomerFirst ? '1. Customer' : `1. ${countersignerLabel}`}
                                                                    </span>
                                                                    <ArrowRight className="h-3.5 w-3.5 text-amber-400" />
                                                                    <span className={`text-sm font-medium ${isCustomerFirst ? 'text-gray-500' : 'text-amber-800'}`}>
                                                                        {isCustomerFirst ? `2. ${countersignerLabel}` : '2. Customer'}
                                                                    </span>
                                                                </div>
                                                                <p className="text-xs text-amber-600 mt-1">
                                                                    {isCustomerFirst
                                                                        ? 'The customer signs first, then the counter-signer below.'
                                                                        : 'The counter-signer below signs first, then the customer.'}
                                                                </p>
                                                            </div>
                                                        );
                                                    })()}

                                                    {signingConfirmStep.step_config_snapshot.require_admin_countersign && countersignerOverride && (
                                                        <div className="rounded-md border border-gray-200 dark:border-zinc-700 px-3 py-2">
                                                            <CountersignerPicker
                                                                adminUsers={adminUsers}
                                                                type={countersignerOverride.type}
                                                                adminUserId={countersignerOverride.adminUserId}
                                                                email={countersignerOverride.email}
                                                                name={countersignerOverride.name}
                                                                onChange={setCountersignerOverride}
                                                                label="Counter-Signer"
                                                                helpText={`Set on the task flow to: ${countersignerSummary(countersignerFromInstanceStep(signingConfirmStep), adminUsers)}. Change it here to use someone else for this document only.`}
                                                            />
                                                        </div>
                                                    )}

                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Joint Customer Email <span className="font-normal normal-case">(optional)</span></label>
                                                        <input
                                                            type="email"
                                                            value={jointCustomerEmail}
                                                            onChange={(e) => setJointCustomerEmail(e.target.value)}
                                                            placeholder="e.g. partner or spouse email address"
                                                            className="block w-full rounded-md border-0 py-2 px-3 text-sm text-gray-900 dark:text-white dark:bg-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600"
                                                        />
                                                        <p className="text-xs text-gray-400 mt-1">If provided, this person will also receive the document to sign via Adobe Sign.</p>
                                                    </div>

                                                    {(signingConfirmStep.step_config_snapshot.field_mappings || []).length > 0 && (
                                                        <div>
                                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Template Fields</p>
                                                            <div className="border border-gray-200 dark:border-zinc-700 rounded-md divide-y divide-gray-200 dark:divide-zinc-700">
                                                                {signingConfirmStep.step_config_snapshot.field_mappings.map((mapping, idx) => {
                                                                    const value = resolveFieldMappingValue(mapping);
                                                                    return (
                                                                        <div key={idx} className="px-3 py-2 flex items-start justify-between gap-3">
                                                                            <div className="min-w-0">
                                                                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{mapping.adobe_field_name}</p>
                                                                                <p className="text-xs text-gray-400">
                                                                                    {mapping.source_type === 'task_data' ? 'From task data' : mapping.source_type === 'static_value' ? 'Static value' : 'Customer enters'}
                                                                                </p>
                                                                            </div>
                                                                            <div className="text-right shrink-0">
                                                                                {value === null ? (
                                                                                    <span className="text-xs text-gray-400 italic">Customer fills in</span>
                                                                                ) : value === '-' || value === '(step not found)' || value === '(field not found)' ? (
                                                                                    <span className="text-xs text-red-500">{value}</span>
                                                                                ) : (
                                                                                    <span className="text-sm text-gray-900 dark:text-white">{value}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 sm:ml-3 sm:w-auto"
                                                onClick={confirmInitiateDocumentSigning}
                                                tabIndex="-1"
                                            >
                                                Create Draft
                                            </button>
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md bg-white dark:bg-zinc-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-700 sm:mt-0 sm:w-auto"
                                                onClick={() => setSigningConfirmModalOpen(false)}
                                                tabIndex="-1"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>

                <ConfirmationModal
                    open={cancelSigningModalOpen}
                    setOpen={setCancelSigningModalOpen}
                    title="Cancel Document Signing"
                    message="Are you sure you want to cancel this document signing? The agreement will be cancelled in Adobe Sign and the step will be reset."
                    confirmText="Cancel Signing"
                    cancelText="Go Back"
                    onConfirm={confirmCancelDocumentSigning}
                    variant="danger"
                />
                <DeleteModal
                    open={deleteModalOpen}
                    setOpen={setDeleteModalOpen}
                    IDToDelete={fieldToDelete?.id}
                    refresh={refreshData}
                    deleteAction="deleteInstanceField"
                    title="Delete Field"
                    message={`Are you sure you want to delete "${fieldToDelete?.name_snapshot}"? This action cannot be undone.`}
                />
                <ConfirmationModal
                    open={deleteInstanceModalOpen}
                    setOpen={setDeleteInstanceModalOpen}
                    title="Delete Task Instance"
                    message="Are you sure you want to permanently delete this task instance? All steps, fields, messages, and history will be removed. This action cannot be undone."
                    note="Any files uploaded to SharePoint will not be deleted and must be removed manually."
                    confirmText="Delete"
                    cancelText="Go Back"
                    onConfirm={handleDeleteInstance}
                    variant="danger"
                />
            </AdminSidebar>
        </>
    );
}
